import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Key, Phone, Eye, EyeOff, Save, AlertCircle, RefreshCw, Trash2, Shield, Plus, Globe, CheckCircle, XCircle, Loader2, Bell, BellOff, ChevronDown, ChevronUp, Settings, Sparkles, Pencil, ArrowRight, Users, Mail } from "lucide-react";
import { toast } from "sonner";
import { fetchSettings, updateSetting, testVoipConnection, getApiKeys, createApiKey, updateApiKey, deleteApiKey, regenerateApiKey, getAiConfig, updateAiConfig } from "../../services/crmServices/settingService";
import { fetchLeadStatuses } from "../../services/crmServices/leadStatusService";
import { fetchEmailTemplates } from "../../services/crmServices/emailTemplateService";
import { fetchDesks } from "../../services/crmServices/deskService";
import { Select } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../hooks/useAuth";
import { initiateSquareTalkCall, initiateVoisoCall, initiateCommpeakCall, initiateCoperatoCall, initiateOctellaCall, initiateCccCall, getSquareTalkCallStatus, endCall } from "../../services/voipServices/voipService";
import Swal from "sweetalert2";
import { AiCallerIntegrations } from "../../components/settings/AiCallerIntegrations";

export function SystemSettings() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showApiKeys, setShowApiKeys] = useState({
        voiso: false,
        commpeak: false,
        coperato: false,
        squaretalk: false,
        octella: false,
        ccc: false
    });
    const [expandedVoipSections, setExpandedVoipSections] = useState({
        voiso: false,
        coperato: false,
        commpeak: false,
        squaretalk: false,
        octella: false,
        ccc: false
    });
    const [expandedAffiliateSection, setExpandedAffiliateSection] = useState(false);
    const [expandedAiSection, setExpandedAiSection] = useState(false);

    // AI Assistant settings
    const [aiConfig, setAiConfig] = useState({
        enabled: false,
        hasApiKey: false,
        keyPrefix: "",
        model: "gpt-4o-mini",
        systemPrompt: "",
        monthlyTokenLimitPerUser: 0,
        allowedRoles: ["admin", "floorManager", "deskManager", "agent"]
    });
    const [aiApiKeyInput, setAiApiKeyInput] = useState("");
    const [showAiKey, setShowAiKey] = useState(false);
    const [aiSaving, setAiSaving] = useState(false);

    const loadAiConfig = async () => {
        try {
            const res = await getAiConfig();
            if (res?.data) {
                setAiConfig(res.data);
                setAiApiKeyInput("");
            }
        } catch (err) {
            console.error("Failed to load AI config", err);
        }
    };

    useEffect(() => {
        if (user?.position?.role === "admin") loadAiConfig();
    }, [user]);

    const handleSaveAiConfig = async () => {
        setAiSaving(true);
        try {
            const payload = {
                enabled: aiConfig.enabled,
                model: aiConfig.model,
                systemPrompt: aiConfig.systemPrompt,
                monthlyTokenLimitPerUser: aiConfig.monthlyTokenLimitPerUser,
                allowedRoles: aiConfig.allowedRoles
            };
            if (aiApiKeyInput && aiApiKeyInput.trim()) {
                payload.apiKey = aiApiKeyInput.trim();
            }
            const res = await updateAiConfig(payload);
            if (res?.data) {
                setAiConfig(res.data);
                setAiApiKeyInput("");
                toast.success("AI Assistant settings saved");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to save AI settings");
        } finally {
            setAiSaving(false);
        }
    };

    const toggleAiRole = (role) => {
        setAiConfig((prev) => {
            const current = prev.allowedRoles || [];
            return {
                ...prev,
                allowedRoles: current.includes(role) ? current.filter((r) => r !== role) : [...current, role]
            };
        });
    };

    const [apiKeys, setApiKeys] = useState({
        voiso: {
            apiKey: "",
            domain: "",
            enabled: false
        },
        commpeak: {
            apiKey: "",
            domain: "",
            crmId: "",
            clientId: "",
            c2cId: "", // Deprecated - kept for backward compatibility
            routeNumber: "00",
            prefix: "00",
            enabled: false
        },
        coperato: {
            domain: "",
            enabled: false
        },
        squaretalk: {
            apiKey: "",
            domain: "",
            accountId: "",
            enabled: false
        },
        octella: {
            apiKey: "",
            virtualNumberGroup: "",
            enabled: false
        },
        ccc: {
            apiKey: "",
            domain: "",
            callerId: "",
            webhookUrl: "",
            enabled: false
        }
    });

    const [whiteIps, setWhiteIps] = useState([]);
    const [newIp, setNewIp] = useState("");
    const [originalApiKeys, setOriginalApiKeys] = useState({});
    const [originalWhiteIps, setOriginalWhiteIps] = useState([]);
    const [testingProvider, setTestingProvider] = useState(null);
    const [testResults, setTestResults] = useState({});
    
    // Test call states
    const [testCallLoading, setTestCallLoading] = useState(false);
    const [testCallResults, setTestCallResults] = useState({});
    const [testCallInputs, setTestCallInputs] = useState({
        agentExtension: "",
        destinationPhone: ""
    });
    const [callStatusPolling, setCallStatusPolling] = useState({}); // Track polling intervals
    const [callStatus, setCallStatus] = useState({}); // Current call status
    const [endingCall, setEndingCall] = useState(false); // Track end call loading state
    
    // Affiliate API Keys state
    const [affiliateApiKeys, setAffiliateApiKeys] = useState([]);
    const [leadStatuses, setLeadStatuses] = useState([]);
    const [mutedAffiliates, setMutedAffiliates] = useState([]);
    const [originalMutedAffiliates, setOriginalMutedAffiliates] = useState([]);
    const [showCreateApiKeyModal, setShowCreateApiKeyModal] = useState(false);
    const [editingApiKey, setEditingApiKey] = useState(null);
    const [editApiKeyData, setEditApiKeyData] = useState({
        name: "",
        defaultStatus: "New",
        routingMode: "none",
        routingDeskId: "",
        emailTemplateId: ""
    });
    const [newApiKeyData, setNewApiKeyData] = useState({
        name: "",
        defaultStatus: "New",
        isActive: true,
        routingMode: "none",
        routingDeskId: "",
        emailTemplateId: ""
    });
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [availableDesks, setAvailableDesks] = useState([]);
    const [generatedApiKey, setGeneratedApiKey] = useState(null); // Store newly generated key for display
    const [apiKeyLoading, setApiKeyLoading] = useState(false);

    useEffect(() => {
        if (user.position.role !== "admin") {
            toast.error("Access denied. Admin privileges required.");
            return;
        }
        loadSettings();
        loadApiKeys();
        loadLeadStatuses();
        loadDesksForRouting();
        loadEmailTemplates();
    }, [user.position]);

    const loadEmailTemplates = async () => {
        try {
            const res = await fetchEmailTemplates();
            const list = res?.data || [];
            setEmailTemplates(Array.isArray(list) ? list.filter((t) => t.enabled !== false) : []);
        } catch (err) {
            console.error("Failed to load email templates", err);
        }
    };

    const loadDesksForRouting = async () => {
        try {
            const res = await fetchDesks();
            const list = res?.data || [];
            setAvailableDesks(Array.isArray(list) ? list : []);
        } catch (err) {
            console.error("Failed to load desks", err);
        }
    };

    const loadSettings = async () => {
        setLoading(true);
        try {
            const response = await fetchSettings();
            if (response?.data?.length) {
                // Load VOIP settings
                if (response.data[0].voip) {
                    setApiKeys(response.data[0].voip);
                    setOriginalApiKeys(response.data[0].voip);
                }

                // Load whitelist IPs
                if (response.data[0].whiteIps) {
                    setWhiteIps(response.data[0].whiteIps);
                    setOriginalWhiteIps(response.data[0].whiteIps);
                }

                // Load muted affiliates
                if (response.data[0].mutedAffiliates) {
                    setMutedAffiliates(response.data[0].mutedAffiliates);
                    setOriginalMutedAffiliates(response.data[0].mutedAffiliates);
                } else {
                    setMutedAffiliates([]);
                    setOriginalMutedAffiliates([]);
                }
            }
        } catch (error) {
            console.error("Error loading settings:", error);
            toast.error("Failed to load settings");
        } finally {
            setLoading(false);
        }
    };

    const handleApiKeyChange = (provider, field, value) => {
        // Trim string values to prevent whitespace issues
        const trimmedValue = typeof value === 'string' ? value.trim() : value;
        
        setApiKeys((prev) => ({
            ...prev,
            [provider]: {
                ...prev[provider],
                [field]: trimmedValue
            }
        }));
    };

    const handleSaveAPIKeys = async () => {
        setLoading(true);
        try {
            const response = await updateSetting({
                voip: apiKeys
            });

            if (response?.status === 200) {
                toast.success("API keys saved successfully");
                setOriginalApiKeys(apiKeys);
                setOriginalWhiteIps(whiteIps);
            } else {
                toast.error("Failed to save API keys");
            }
        } catch (error) {
            console.error("Error saving settings:", error);
            toast.error("Failed to save settings");
        } finally {
            setLoading(false);
        }
    };

    const handleClearApiKey = (provider) => {
        if (
            window.confirm(
                `Are you sure you want to clear the ${provider} API key? This will disable ${provider} integration.`
            )
        ) {
            handleApiKeyChange(provider, "apiKey", "");
            handleApiKeyChange(provider, "enabled", false);
        }
    };

    const handleTestConnection = async (provider) => {
        setTestingProvider(provider);
        setTestResults((prev) => ({ ...prev, [provider]: null }));

        try {
            const providerSettings = apiKeys[provider] || {};
            const response = await testVoipConnection(provider, providerSettings);

            if (response?.success) {
                setTestResults((prev) => ({
                    ...prev,
                    [provider]: {
                        success: true,
                        message: response.message || `Successfully connected to ${provider}`,
                        details: response.data
                    }
                }));
                toast.success(`Connection test successful for ${provider}`);
                // Reload settings to get updated status
                loadSettings();
            } else {
                // Extract detailed error information
                const errorMessage = response.error || `Connection test failed for ${provider}`;
                const errorReason = response.reason || response.error;
                const troubleshooting = response.troubleshooting || {};
                const details = response.details || {};
                
                setTestResults((prev) => ({
                    ...prev,
                    [provider]: {
                        success: false,
                        message: errorMessage,
                        reason: errorReason,
                        details: details,
                        troubleshooting: troubleshooting,
                        endpoint: troubleshooting.endpoint,
                        accountId: troubleshooting.accountId,
                        squaretalkError: troubleshooting.squaretalkError,
                        squaretalkErrorCode: troubleshooting.squaretalkErrorCode
                    }
                }));
                
                // Show detailed error toast
                const toastMessage = errorReason || errorMessage;
                toast.error(toastMessage, {
                    duration: 8000, // Show longer for detailed errors
                    description: troubleshooting.squaretalkError ? `SquareTalk error: ${troubleshooting.squaretalkError}` : undefined
                });
            }
        } catch (error) {
            console.error(`Test connection error for ${provider}:`, error);
            const errorData = error?.response?.data || {};
            const errorMessage = errorData.error || error?.response?.data?.reason || error?.message || `Failed to test connection for ${provider}`;
            const errorReason = errorData.reason || errorData.error || errorMessage;
            const troubleshooting = errorData.troubleshooting || {};
            const details = errorData.details || {};

            setTestResults((prev) => ({
                ...prev,
                [provider]: {
                    success: false,
                    message: errorMessage,
                    reason: errorReason,
                    details: details,
                    troubleshooting: troubleshooting,
                    endpoint: troubleshooting.endpoint,
                    accountId: troubleshooting.accountId,
                    squaretalkError: troubleshooting.squaretalkError,
                    squaretalkErrorCode: troubleshooting.squaretalkErrorCode
                }
            }));
            
            // Show detailed error toast
            const toastMessage = errorReason || errorMessage;
            toast.error(toastMessage, {
                duration: 8000,
                description: troubleshooting.squaretalkError ? `SquareTalk error: ${troubleshooting.squaretalkError}` : undefined
            });
        } finally {
            setTestingProvider(null);
        }
    };

    const handleTestCall = async (provider) => {
        if (!testCallInputs.agentExtension || !testCallInputs.destinationPhone) {
            toast.error("Please enter both agent extension and destination phone number");
            return;
        }

        setTestCallLoading(true);
        setTestCallResults((prev) => ({ ...prev, [provider]: null }));

        try {
            let callResult;
            // For test calls, pass extension directly instead of agentId (user ID)
            const callParams = {
                phoneNumber: testCallInputs.destinationPhone,
                extension: testCallInputs.agentExtension, // Pass extension directly for test calls
                leadId: null // Test call doesn't need a lead ID
            };

            // Call the appropriate provider's initiate function
            switch (provider) {
                case "squaretalk":
                    callResult = await initiateSquareTalkCall(callParams);
                    break;
                case "voiso":
                    callResult = await initiateVoisoCall(callParams);
                    break;
                case "commpeak":
                    callResult = await initiateCommpeakCall(callParams);
                    break;
                case "coperato":
                    callResult = await initiateCoperatoCall(callParams);
                    break;
                case "ccc":
                    callResult = await initiateCccCall(callParams);
                    break;
                case "octella":
                    callResult = await initiateOctellaCall(callParams);
                    break;
                default:
                    toast.error("Provider not supported for test calls");
                    return;
            }

            if (callResult?.success) {
                const callId = callResult.callId || callResult.data?.call_id || callResult.data?.id || callResult.data?.callId;
                const fullResponse = callResult.data || callResult.fullResponse || callResult;
                const extension = testCallInputs.agentExtension;
                
                setTestCallResults((prev) => ({
                    ...prev,
                    [provider]: {
                        success: true,
                        message: callResult.message || "Call initiated successfully",
                        callId: callId,
                        extension: extension,
                        fullResponse: fullResponse,
                        rawResponse: callResult.rawResponse,
                        httpStatus: callResult.httpStatus,
                        note: callResult.note,
                        extensionStatus: callResult.extensionStatus,
                        description: callResult.description
                    }
                }));
                
                // Start polling for SquareTalk status
                if (provider === "squaretalk" && extension) {
                    startCallStatusPolling(provider, extension);
                }
                
                toast.success(`Test call initiated successfully for ${provider}${callId ? ` (Call ID: ${callId})` : ''}`, {
                    description: callResult.note || "Monitoring call status..."
                });
            } else {
                const errorMessage = callResult?.error || callResult?.message || "Failed to initiate test call";
                const errorDetails = callResult?.details || callResult?.responseBody || callResult;
                setTestCallResults((prev) => ({
                    ...prev,
                    [provider]: {
                        success: false,
                        message: errorMessage,
                        details: errorDetails,
                        endpoint: callResult?.endpoint,
                        requestBody: callResult?.requestBody,
                        httpStatus: callResult?.httpStatus
                    }
                }));
                toast.error(`Test call failed: ${errorMessage}`, {
                    duration: 10000,
                    description: errorDetails?.message || errorDetails?.error || "Check console for details"
                });
                console.error("[Test Call] Error details:", errorDetails);
            }
        } catch (error) {
            console.error(`Test call error for ${provider}:`, error);
            console.error(`Full error response:`, error?.response?.data);
            const errorMessage = error?.response?.data?.error || error?.message || `Failed to initiate test call for ${provider}`;
            const errorDetails = error?.response?.data;
            setTestCallResults((prev) => ({
                ...prev,
                [provider]: {
                    success: false,
                    message: errorMessage,
                    details: errorDetails
                }
            }));
            toast.error(`Test call failed: ${errorMessage}`, {
                description: errorDetails ? JSON.stringify(errorDetails) : undefined,
                duration: 10000
            });
        } finally {
            setTestCallLoading(false);
        }
    };

    // Start polling call status for SquareTalk
    const startCallStatusPolling = (provider, extension) => {
        // Clear any existing polling for this provider
        if (callStatusPolling[provider]) {
            clearInterval(callStatusPolling[provider]);
        }

        // Initial status check
        checkCallStatus(provider, extension);

        // Poll every 3 seconds
        const interval = setInterval(() => {
            checkCallStatus(provider, extension);
        }, 3000);

        setCallStatusPolling((prev) => ({
            ...prev,
            [provider]: interval
        }));
    };

    // Stop polling call status
    const stopCallStatusPolling = (provider) => {
        if (callStatusPolling[provider]) {
            clearInterval(callStatusPolling[provider]);
            setCallStatusPolling((prev) => {
                const newPolling = { ...prev };
                delete newPolling[provider];
                return newPolling;
            });
        }
    };

    // Check call status
    const checkCallStatus = async (provider, extension) => {
        try {
            if (provider === "squaretalk" && extension) {
                const statusResult = await getSquareTalkCallStatus(extension);
                
                if (statusResult?.success) {
                    const statusInfo = statusResult.statusInfo || statusResult.data?.meta;
                    const extensionStatus = statusInfo?.extension_status || statusResult.data?.meta?.extension_status;
                    const connectivity = statusInfo?.connectivity || statusResult.data?.meta?.Connectivity;
                    const description = statusInfo?.description || statusResult.data?.meta?.description;
                    
                    // Get previous status before updating
                    const prevStatus = callStatus[provider]?.extensionStatus;
                    
                    setCallStatus((prev) => ({
                        ...prev,
                        [provider]: {
                            extensionStatus: extensionStatus,
                            connectivity: connectivity,
                            description: description,
                            status: statusResult.data?.status,
                            isConnected: statusResult.isConnected,
                            isRinging: statusResult.isRinging,
                            isBusy: statusResult.isBusy,
                            isDisconnected: statusResult.isDisconnected,
                            lastUpdated: new Date()
                        }
                    }));

                    // Update test call results with latest status
                    setTestCallResults((prev) => ({
                        ...prev,
                        [provider]: {
                            ...prev[provider],
                            extensionStatus: extensionStatus,
                            description: description,
                            connectivity: connectivity
                        }
                    }));

                    // Show toast notification for important status changes
                    // Only show toast if status actually changed
                    if (prevStatus && prevStatus !== extensionStatus) {
                        if (extensionStatus === "Connected" || statusResult.isConnected) {
                            toast.success("Call Answered", {
                                description: `Call is now connected. ${description || ''}`
                            });
                        } else if (extensionStatus === "DISCONNECTED" || statusResult.isDisconnected) {
                            toast.info("Call Ended", {
                                description: "The call has been disconnected"
                            });
                            stopCallStatusPolling(provider);
                        } else if (extensionStatus === "Busy") {
                            toast.warning("Extension Busy", {
                                description: "The extension is currently busy"
                            });
                        } else if (extensionStatus === "ringing" && prevStatus !== "ringing") {
                            toast.info("Extension Ringing", {
                                description: "Waiting for call to be answered..."
                            });
                        }
                    }

                    // Stop polling if call is disconnected or failed
                    // Continue polling for other statuses (ringing, Connected, Busy) to catch status changes
                    if (statusResult.isDisconnected || extensionStatus === "DISCONNECTED" || statusResult.data?.status === "failed") {
                        stopCallStatusPolling(provider);
                        if (prevStatus !== extensionStatus) {
                            toast.info("Status monitoring stopped", {
                                description: "Call has ended or failed"
                            });
                        }
                    }
                }
            }
        } catch (error) {
            // Don't stop polling on error, might be temporary network issue
        }
    };

    // Handle end call
    const handleEndCall = async (provider, extension) => {
        if (!extension) {
            toast.error("Extension is required to end the call");
            return;
        }

        setEndingCall(true);
        try {
            // For SquareTalk, we use extension as the callId
            const result = await endCall(extension, provider, extension);
            
            if (result?.success) {
                toast.success("Call Ended", {
                    description: "The call has been terminated successfully"
                });
                
                // Stop status polling
                stopCallStatusPolling(provider);
                
                // Update status to disconnected
                setCallStatus((prev) => ({
                    ...prev,
                    [provider]: {
                        ...prev[provider],
                        extensionStatus: "DISCONNECTED",
                        isDisconnected: true,
                        lastUpdated: new Date()
                    }
                }));
                
                // Update test call results
                setTestCallResults((prev) => ({
                    ...prev,
                    [provider]: {
                        ...prev[provider],
                        extensionStatus: "DISCONNECTED"
                    }
                }));
            } else {
                toast.error("Failed to End Call", {
                    description: result?.error || "Could not terminate the call"
                });
            }
        } catch (error) {
            toast.error("Failed to End Call", {
                description: error.message || "An error occurred while ending the call"
            });
        } finally {
            setEndingCall(false);
        }
    };

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            Object.values(callStatusPolling).forEach(interval => {
                if (interval) clearInterval(interval);
            });
        };
    }, [callStatusPolling]);

    // IP Whitelist functions
    const validateIp = (ip) => {
        const ipRegex = /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        const cidrRegex =
            /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\/([1-9]|[1-2][0-9]|3[0-2])$/;

        return ipRegex.test(ip) || cidrRegex.test(ip);
    };

    const handleAddIp = () => {
        const trimmedIp = newIp.trim();

        if (!trimmedIp) {
            toast.error("Please enter an IP address");
            return;
        }

        if (!validateIp(trimmedIp)) {
            toast.error("Please enter a valid IP address (e.g., 192.168.1.1 or 192.168.1.0/24)");
            return;
        }

        if (whiteIps.includes(trimmedIp)) {
            toast.error("This IP address is already in the whitelist");
            return;
        }
        const newWhiteIps = [...whiteIps, trimmedIp];
        updateSetting({ whiteIps: newWhiteIps }).then((response) => {
            if (response?.status === 200) {
                setWhiteIps(newWhiteIps);
                setNewIp("");
                toast.success("IP address added to whitelist");
            } else {
                toast.error("Failed to update IP whitelist");
                return;
            }
        });
    };

    const handleRemoveIp = (ipToRemove) => {
        if (window.confirm(`Are you sure you want to remove ${ipToRemove} from the whitelist?`)) {
            const newWhiteIps = whiteIps.filter((ip) => ip !== ipToRemove);
            updateSetting({ whiteIps: newWhiteIps }).then((response) => {
                if (response?.status === 200) {
                    setWhiteIps(newWhiteIps);
                    toast.success("IP address removed from whitelist");
                } else {
                    toast.error("Failed to remove IP from whitelist");
                }
            }).catch((error) => {
                console.error("Error removing IP:", error);
                toast.error("Failed to remove IP from whitelist");
            });
        }
    };

    const hasUnsavedChanges = () => {
        return (
            JSON.stringify(apiKeys) !== JSON.stringify(originalApiKeys) ||
            JSON.stringify(whiteIps) !== JSON.stringify(originalWhiteIps) ||
            JSON.stringify(mutedAffiliates.sort()) !== JSON.stringify(originalMutedAffiliates.sort())
        );
    };

    const handleToggleAffiliateNotification = (affiliateName) => {
        setMutedAffiliates((prev) => {
            if (prev.includes(affiliateName)) {
                // Unmute: remove from muted list
                return prev.filter((name) => name !== affiliateName);
            } else {
                // Mute: add to muted list
                return [...prev, affiliateName];
            }
        });
    };

    const handleSaveNotificationSettings = async () => {
        setLoading(true);
        try {
            const response = await updateSetting({
                mutedAffiliates: mutedAffiliates
            });

            if (response?.status === 200) {
                toast.success("Notification settings saved successfully");
                setOriginalMutedAffiliates(mutedAffiliates);
            } else {
                toast.error("Failed to save notification settings");
            }
        } catch (error) {
            console.error("Error saving notification settings:", error);
            toast.error("Failed to save notification settings");
        } finally {
            setLoading(false);
        }
    };

    const loadApiKeys = async () => {
        try {
            const response = await getApiKeys();
            if (response?.status === 200) {
                setAffiliateApiKeys(response.data || []);
            } else {
                console.error("Unexpected response:", response);
                // If no API keys exist yet, set empty array
                setAffiliateApiKeys([]);
            }
        } catch (error) {
            console.error("Error loading API keys:", error);
            // If it's a 404 or settings don't exist yet, just set empty array
            if (error.response?.status === 404 || error.response?.status === 400) {
                setAffiliateApiKeys([]);
            } else {
                toast.error("Failed to load API keys: " + (error.response?.data?.message || error.message));
            }
        }
    };

    const loadLeadStatuses = async () => {
        try {
            const response = await fetchLeadStatuses();
            if (response?.status === 200) {
                setLeadStatuses(response.data || []);
            }
        } catch (error) {
            console.error("Error loading lead statuses:", error);
        }
    };

    const handleCreateApiKey = async () => {
        if (!newApiKeyData.name.trim()) {
            toast.error("Affiliate name is required");
            return;
        }

        setApiKeyLoading(true);
        try {
            const response = await createApiKey(newApiKeyData);
            if (response?.status === 200) {
                toast.success("API key created successfully");
                setGeneratedApiKey(response.data.apiKey); // Store the generated key
                await loadApiKeys();
                // Keep modal open to show the key
            } else {
                toast.error(response?.message || "Failed to create API key");
            }
        } catch (error) {
            console.error("Error creating API key:", error);
            toast.error(error.response?.data?.message || "Failed to create API key");
        } finally {
            setApiKeyLoading(false);
        }
    };

    const handleCloseApiKeyModal = () => {
        setShowCreateApiKeyModal(false);
        setGeneratedApiKey(null);
        setNewApiKeyData({ name: "", defaultStatus: "New", isActive: true, routingMode: "none", routingDeskId: "", emailTemplateId: "" });
    };

    // ── Edit existing API key ───────────────────────────────────────────────
    const openEditApiKey = (key) => {
        setEditingApiKey(key);
        setEditApiKeyData({
            name: key.name || "",
            defaultStatus: key.defaultStatus || "New",
            routingMode: key.routingMode || "none",
            routingDeskId: key.routingDeskId ? String(key.routingDeskId) : "",
            emailTemplateId: key.emailTemplateId ? String(key.emailTemplateId) : ""
        });
    };

    const closeEditApiKey = () => {
        setEditingApiKey(null);
        setEditApiKeyData({ name: "", defaultStatus: "New", routingMode: "none", routingDeskId: "", emailTemplateId: "" });
    };

    const handleSaveApiKeyEdit = async () => {
        if (!editingApiKey?._id) return;
        if (!editApiKeyData.name.trim()) {
            toast.error("Name is required");
            return;
        }
        if (editApiKeyData.routingMode !== "none" && !editApiKeyData.routingDeskId) {
            toast.error("Please pick a desk for the selected routing mode");
            return;
        }
        await handleUpdateApiKey(editingApiKey._id, {
            name: editApiKeyData.name.trim(),
            defaultStatus: editApiKeyData.defaultStatus,
            routingMode: editApiKeyData.routingMode,
            routingDeskId: editApiKeyData.routingMode === "none" ? null : editApiKeyData.routingDeskId,
            emailTemplateId: editApiKeyData.emailTemplateId || null
        });
        closeEditApiKey();
    };

    const handleUpdateApiKey = async (keyId, updates) => {
        setApiKeyLoading(true);
        try {
            const response = await updateApiKey(keyId, updates);
            if (response?.status === 200) {
                toast.success("API key updated successfully");
                await loadApiKeys();
                setEditingApiKey(null);
            } else {
                toast.error(response?.message || "Failed to update API key");
            }
        } catch (error) {
            console.error("Error updating API key:", error);
            toast.error(error.response?.data?.message || "Failed to update API key");
        } finally {
            setApiKeyLoading(false);
        }
    };

    const handleDeleteApiKey = async (keyId) => {
        const result = await Swal.fire({
            title: "Delete API Key?",
            text: "This will permanently delete the API key. Affiliates using this key will no longer be able to access the API.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it"
        });

        if (result.isConfirmed) {
            setApiKeyLoading(true);
            try {
                const response = await deleteApiKey(keyId);
                if (response?.status === 200) {
                    toast.success("API key deleted successfully");
                    await loadApiKeys();
                } else {
                    toast.error(response?.message || "Failed to delete API key");
                }
            } catch (error) {
                console.error("Error deleting API key:", error);
                toast.error(error.response?.data?.message || "Failed to delete API key");
            } finally {
                setApiKeyLoading(false);
            }
        }
    };

    const handleRegenerateApiKey = async (keyId) => {
        const result = await Swal.fire({
            title: "Regenerate API Key?",
            text: "This will generate a new API key. The old key will no longer work. Make sure to update the affiliate with the new key.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, regenerate it"
        });

        if (result.isConfirmed) {
            setApiKeyLoading(true);
            try {
                const response = await regenerateApiKey(keyId);
                if (response?.status === 200) {
                    toast.success("API key regenerated successfully");
                    setGeneratedApiKey(response.data.apiKey);
                    setEditingApiKey(keyId);
                    await loadApiKeys();
                    // Show the new key
                    Swal.fire({
                        title: "New API Key Generated",
                        html: `<div class="text-left">
                            <p class="mb-2">Copy this key now. You won't be able to see it again!</p>
                            <div class="bg-gray-100 p-3 rounded font-mono text-sm break-all">${response.data.apiKey}</div>
                        </div>`,
                        icon: "success",
                        confirmButtonText: "I've copied it"
                    });
                } else {
                    toast.error(response?.message || "Failed to regenerate API key");
                }
            } catch (error) {
                console.error("Error regenerating API key:", error);
                toast.error(error.response?.data?.message || "Failed to regenerate API key");
            } finally {
                setApiKeyLoading(false);
            }
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard!");
    };

    const toggleVoipSection = (provider) => {
        setExpandedVoipSections((prev) => ({
            ...prev,
            [provider]: !prev[provider]
        }));
    };

    if (user.position.role !== "admin") {
        return (
            <div className="container mx-auto px-4 sm:px-6">
                <div className="max-w-md mx-auto mt-12 bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
                    <div className="p-3 bg-red-50 rounded-full w-fit mx-auto mb-4">
                        <AlertCircle className="h-8 w-8 text-red-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Denied</h3>
                    <p className="text-sm text-gray-500">Administrator privileges required to access this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-6xl mx-auto px-4 pb-8">
            {/* Page Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-md">
                            <Settings className="h-5 w-5 text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">System Configuration</h1>
                            <p className="text-sm text-gray-500">Manage VOIP integrations, security & API access</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Overview Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
                    <div className="p-2.5 bg-blue-50 rounded-lg">
                        <Phone className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900">4</p>
                        <p className="text-xs text-gray-500 font-medium">VOIP Providers</p>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
                    <div className="p-2.5 bg-purple-50 rounded-lg">
                        <Shield className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900">{whiteIps.length}</p>
                        <p className="text-xs text-gray-500 font-medium">Whitelisted IPs</p>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-4">
                    <div className="p-2.5 bg-green-50 rounded-lg">
                        <Key className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-gray-900">{affiliateApiKeys.length}</p>
                        <p className="text-xs text-gray-500 font-medium">Affiliate API Keys</p>
                    </div>
                </div>
            </div>

            {/* IP Whitelist Section */}
            <div className="mb-6 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <Shield className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900">IP Whitelist</h3>
                                <p className="text-xs text-gray-500">Control which IPs can access the system</p>
                            </div>
                        </div>
                        <Badge variant="outline" className="text-blue-600 border-blue-200 text-xs">
                            {whiteIps.length} {whiteIps.length === 1 ? 'Address' : 'Addresses'}
                        </Badge>
                    </div>
                </div>
                <div className="p-5 space-y-4">
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2.5">
                        <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                        <p className="text-xs text-amber-700 leading-relaxed">
                            Only IP addresses in this whitelist will be allowed to access the system. Be careful
                            when modifying this list to avoid locking yourself out.
                        </p>
                    </div>

                    <div className="flex gap-2">
                        <Input
                            value={newIp}
                            onChange={(e) => setNewIp(e.target.value)}
                            placeholder="Enter IP address (e.g., 192.168.1.1 or 192.168.1.0/24)"
                            onKeyPress={(e) => e.key === "Enter" && handleAddIp()}
                            className="flex-1"
                        />
                        <Button
                            onClick={handleAddIp}
                            disabled={!newIp.trim()}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Add IP
                        </Button>
                    </div>

                    {whiteIps.length > 0 ? (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {whiteIps.map((ip, index) => (
                                <div
                                    key={index}
                                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50/50 hover:bg-blue-50/50 hover:border-blue-200 transition-all group"
                                >
                                    <div className="flex items-center gap-2.5">
                                        <div className="p-1.5 bg-blue-100 rounded-md">
                                            <Globe className="h-3.5 w-3.5 text-blue-600" />
                                        </div>
                                        <span className="font-mono text-sm text-gray-800">{ip}</span>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveIp(ip)}
                                        className="text-gray-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
                            <div className="p-3 bg-blue-50 rounded-full w-fit mx-auto mb-3">
                                <Shield className="h-7 w-7 text-blue-400" />
                            </div>
                            <p className="text-sm text-gray-600 font-medium">No IP addresses in whitelist</p>
                            <p className="text-xs text-gray-400 mt-1">Add IP addresses to restrict system access</p>
                        </div>
                    )}
                </div>
            </div>

            {/* VOIP API Keys Section */}
            <div className="mb-6 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-indigo-100 rounded-lg">
                                <Phone className="h-4 w-4 text-indigo-600" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900">VOIP Providers</h3>
                                <p className="text-xs text-gray-500">Configure voice calling integrations</p>
                            </div>
                        </div>
                        <Badge variant="outline" className="text-indigo-600 border-indigo-200 text-xs">
                            4 Providers
                        </Badge>
                    </div>
                </div>
                <div className="p-5 space-y-3">
                    {/* Voiso API Key */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 transition-colors">
                        <div 
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => toggleVoipSection('voiso')}
                        >
                            <div className="flex items-center space-x-3">
                                <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <Phone className="h-4 w-4 text-blue-600" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900">Voiso</h3>
                                    <p className="text-xs text-gray-500">Voice calling integration</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {apiKeys.voiso.enabled && (
                                    <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Active</Badge>
                                )}
                                {expandedVoipSections.voiso ? (
                                    <ChevronUp className="h-4 w-4 text-gray-400" />
                                ) : (
                                    <ChevronDown className="h-4 w-4 text-gray-400" />
                                )}
                            </div>
                        </div>

                        {expandedVoipSections.voiso && (
                        <div className="p-6 pt-0 space-y-4 border-t">
                            <div>
                                <Label htmlFor="voiso-api-key">API Key</Label>
                                <div className="flex space-x-2">
                                    <Input
                                        id="voiso-api-key"
                                        type={showApiKeys.voiso ? "text" : "password"}
                                        value={apiKeys.voiso.apiKey}
                                        onChange={(e) => handleApiKeyChange("voiso", "apiKey", e.target.value)}
                                        placeholder="Enter Voiso API Key"
                                        className="flex-1"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowApiKeys((prev) => ({ ...prev, voiso: !prev.voiso }))}
                                    >
                                        {showApiKeys.voiso ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </Button>
                                    {apiKeys.voiso.apiKey && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleClearApiKey("voiso")}
                                            className="text-red-600 hover:text-red-700"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="voiso-domain">Domain</Label>
                                <Input
                                    id="voiso-domain"
                                    type="text"
                                    value={apiKeys.voiso.domain}
                                    onChange={(e) => handleApiKeyChange("voiso", "domain", e.target.value)}
                                    placeholder="Enter Voiso Domain (e.g., your-company.voiso.com)"
                                    className="flex-1"
                                />
                            </div>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="voiso-enabled"
                                    checked={apiKeys.voiso.enabled}
                                    onChange={(e) => handleApiKeyChange("voiso", "enabled", e.target.checked)}
                                    className="rounded"
                                />
                                <Label htmlFor="voiso-enabled">Enable Voiso Integration</Label>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleTestConnection("voiso")}
                                    disabled={testingProvider === "voiso" || !apiKeys.voiso.apiKey || !apiKeys.voiso.domain}
                                    className="flex items-center space-x-2"
                                >
                                    {testingProvider === "voiso" ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            <span>Testing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Phone className="h-4 w-4" />
                                            <span>Test Connection</span>
                                        </>
                                    )}
                                </Button>
                                {testResults.voiso && (
                                    <div className={`flex items-center space-x-2 ${testResults.voiso.success ? "text-green-600" : "text-red-600"}`}>
                                        {testResults.voiso.success ? (
                                            <CheckCircle className="h-4 w-4" />
                                        ) : (
                                            <XCircle className="h-4 w-4" />
                                        )}
                                        <span className="text-sm font-medium">{testResults.voiso.success ? "Connected" : "Failed"}</span>
                                    </div>
                                )}
                            </div>

                            {testResults.voiso && !testResults.voiso.success && testResults.voiso.reason && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-800 font-medium">Connection Failed</p>
                                    <p className="text-xs text-red-600 mt-1">{testResults.voiso.reason}</p>
                                </div>
                            )}

                            {/* Test Call Section for Voiso */}
                            <div className="mt-6 pt-4 border-t space-y-4">
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Test Call</h4>
                                    <p className="text-xs text-gray-500 mb-3">
                                        Test the Voiso integration by initiating a real call. Enter the agent ID and destination phone number.
                                    </p>
                                    <div className="space-y-3">
                                        <div>
                                            <Label htmlFor="test-agent-id-voiso">Agent ID</Label>
                                            <Input
                                                id="test-agent-id-voiso"
                                                type="text"
                                                value={testCallInputs.agentExtension}
                                                onChange={(e) => setTestCallInputs((prev) => ({ ...prev, agentExtension: e.target.value }))}
                                                placeholder="Enter Voiso agent ID (e.g., agent@company)"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="test-destination-phone-voiso">Destination Phone Number</Label>
                                            <Input
                                                id="test-destination-phone-voiso"
                                                type="tel"
                                                value={testCallInputs.destinationPhone}
                                                onChange={(e) => setTestCallInputs((prev) => ({ ...prev, destinationPhone: e.target.value }))}
                                                placeholder="Enter phone number (e.g., +1234567890)"
                                            />
                                        </div>
                                        <Button
                                            type="button"
                                            variant="default"
                                            onClick={() => handleTestCall("voiso")}
                                            disabled={testCallLoading || !testCallInputs.agentExtension || !testCallInputs.destinationPhone || !apiKeys.voiso.apiKey || !apiKeys.voiso.domain}
                                            className="w-full flex items-center justify-center space-x-2"
                                        >
                                            {testCallLoading ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    <span>Initiating Call...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Phone className="h-4 w-4" />
                                                    <span>Test Call</span>
                                                </>
                                            )}
                                        </Button>
                                        {testCallResults.voiso && (
                                            <div className={`p-3 rounded-lg border ${testCallResults.voiso.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                                                <div className="flex items-center space-x-2 mb-2">
                                                    {testCallResults.voiso.success ? (
                                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                                    ) : (
                                                        <XCircle className="h-4 w-4 text-red-600" />
                                                    )}
                                                    <span className={`text-sm font-medium ${testCallResults.voiso.success ? "text-green-800" : "text-red-800"}`}>
                                                        {testCallResults.voiso.success ? "Call Initiated Successfully" : "Call Failed"}
                                                    </span>
                                                </div>
                                                {testCallResults.voiso.message && (
                                                    <p className={`text-xs ${testCallResults.voiso.success ? "text-green-600" : "text-red-600"}`}>
                                                        {testCallResults.voiso.message}
                                                    </p>
                                                )}
                                                {testCallResults.voiso.callId && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        Call ID: {testCallResults.voiso.callId}
                                                    </p>
                                                )}
                                                {testCallResults.voiso.extension && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        Agent ID: {testCallResults.voiso.extension}
                                                    </p>
                                                )}
                                                {/* Error Details */}
                                                {!testCallResults.voiso.success && testCallResults.voiso.details && (
                                                    <details className="mt-2 text-xs">
                                                        <summary className="cursor-pointer text-red-700 hover:text-red-900 font-medium">
                                                            View Error Details
                                                        </summary>
                                                        <div className="mt-2 space-y-1">
                                                            {testCallResults.voiso.endpoint && (
                                                                <p className="text-xs text-gray-600">
                                                                    <span className="font-medium">Endpoint:</span> {testCallResults.voiso.endpoint}
                                                                </p>
                                                            )}
                                                            {testCallResults.voiso.httpStatus && (
                                                                <p className="text-xs text-gray-600">
                                                                    <span className="font-medium">HTTP Status:</span> {testCallResults.voiso.httpStatus}
                                                                </p>
                                                            )}
                                                            <pre className="mt-2 p-2 bg-red-100 rounded text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                                                {JSON.stringify(testCallResults.voiso.details, null, 2)}
                                                            </pre>
                                                        </div>
                                                    </details>
                                                )}
                                                {/* Technical Details (Expandable) */}
                                                {testCallResults.voiso.success && testCallResults.voiso.fullResponse && (
                                                    <details className="mt-3 text-xs">
                                                        <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                                                            Technical Details
                                                        </summary>
                                                        <div className="mt-2 space-y-1">
                                                            {testCallResults.voiso.httpStatus && (
                                                                <p className="text-xs text-gray-600">
                                                                    HTTP Status: {testCallResults.voiso.httpStatus}
                                                                </p>
                                                            )}
                                                            {testCallResults.voiso.fullResponse && (
                                                                <details className="mt-2">
                                                                    <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                                                                        View Full API Response
                                                                    </summary>
                                                                    <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                                                        {JSON.stringify(testCallResults.voiso.fullResponse, null, 2)}
                                                                    </pre>
                                                                </details>
                                                            )}
                                                        </div>
                                                    </details>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}
                    </div>

                    {/* Coperato Domain */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 transition-colors">
                        <div 
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => toggleVoipSection('coperato')}
                        >
                            <div className="flex items-center space-x-3">
                                <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
                                    <Globe className="h-4 w-4 text-orange-600" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900">Coperato</h3>
                                    <p className="text-xs text-gray-500">Domain-based voice calling</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {apiKeys.coperato?.enabled && (
                                    <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Active</Badge>
                                )}
                                {expandedVoipSections.coperato ? (
                                    <ChevronUp className="h-4 w-4 text-gray-400" />
                                ) : (
                                    <ChevronDown className="h-4 w-4 text-gray-400" />
                                )}
                            </div>
                        </div>

                        {expandedVoipSections.coperato && (
                        <div className="p-6 pt-0 space-y-4 border-t">
                            <div>
                                <Label htmlFor="coperato-domain">Domain</Label>
                                <Input
                                    id="coperato-domain"
                                    type="text"
                                    value={apiKeys.coperato?.domain}
                                    onChange={(e) => handleApiKeyChange("coperato", "domain", e.target.value)}
                                    placeholder="Enter Coperato Domain (e.g., your-company.coperato.com)"
                                    className="flex-1"
                                />
                            </div>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="coperato-enabled"
                                    checked={apiKeys.coperato?.enabled}
                                    onChange={(e) => handleApiKeyChange("coperato", "enabled", e.target.checked)}
                                    className="rounded"
                                />
                                <Label htmlFor="coperato-enabled">Enable Coperato Integration</Label>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleTestConnection("coperato")}
                                    disabled={testingProvider === "coperato" || !apiKeys.coperato?.domain}
                                    className="flex items-center space-x-2"
                                >
                                    {testingProvider === "coperato" ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            <span>Testing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Phone className="h-4 w-4" />
                                            <span>Test Connection</span>
                                        </>
                                    )}
                                </Button>
                                {testResults.coperato && (
                                    <div className={`flex items-center space-x-2 ${testResults.coperato.success ? "text-green-600" : "text-red-600"}`}>
                                        {testResults.coperato.success ? (
                                            <CheckCircle className="h-4 w-4" />
                                        ) : (
                                            <XCircle className="h-4 w-4" />
                                        )}
                                        <span className="text-sm font-medium">{testResults.coperato.success ? "Connected" : "Failed"}</span>
                                    </div>
                                )}
                            </div>

                            {testResults.coperato && !testResults.coperato.success && testResults.coperato.reason && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-800 font-medium">Connection Failed</p>
                                    <p className="text-xs text-red-600 mt-1">{testResults.coperato.reason}</p>
                                </div>
                            )}

                            {/* Test Call Section for Coperato */}
                            <div className="mt-6 pt-4 border-t space-y-4">
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Test Call</h4>
                                    <p className="text-xs text-gray-500 mb-3">
                                        Test the Coperato integration by initiating a real call. Enter the extension and destination phone number.
                                    </p>
                                    <div className="space-y-3">
                                        <div>
                                            <Label htmlFor="test-extension-coperato">Extension</Label>
                                            <Input
                                                id="test-extension-coperato"
                                                type="text"
                                                value={testCallInputs.agentExtension}
                                                onChange={(e) => setTestCallInputs((prev) => ({ ...prev, agentExtension: e.target.value }))}
                                                placeholder="Enter extension (e.g., 1001)"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="test-destination-phone-coperato">Destination Phone Number</Label>
                                            <Input
                                                id="test-destination-phone-coperato"
                                                type="tel"
                                                value={testCallInputs.destinationPhone}
                                                onChange={(e) => setTestCallInputs((prev) => ({ ...prev, destinationPhone: e.target.value }))}
                                                placeholder="Enter phone number (e.g., +1234567890)"
                                            />
                                        </div>
                                        <Button
                                            type="button"
                                            variant="default"
                                            onClick={() => handleTestCall("coperato")}
                                            disabled={testCallLoading || !testCallInputs.agentExtension || !testCallInputs.destinationPhone || !apiKeys.coperato?.domain}
                                            className="w-full flex items-center justify-center space-x-2"
                                        >
                                            {testCallLoading ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    <span>Initiating Call...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Phone className="h-4 w-4" />
                                                    <span>Test Call</span>
                                                </>
                                            )}
                                        </Button>
                                        {testCallResults.coperato && (
                                            <div className={`p-3 rounded-lg border ${testCallResults.coperato.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                                                <div className="flex items-center space-x-2 mb-2">
                                                    {testCallResults.coperato.success ? (
                                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                                    ) : (
                                                        <XCircle className="h-4 w-4 text-red-600" />
                                                    )}
                                                    <span className={`text-sm font-medium ${testCallResults.coperato.success ? "text-green-800" : "text-red-800"}`}>
                                                        {testCallResults.coperato.success ? "Call Initiated Successfully" : "Call Failed"}
                                                    </span>
                                                </div>
                                                {testCallResults.coperato.message && (
                                                    <p className={`text-xs ${testCallResults.coperato.success ? "text-green-600" : "text-red-600"}`}>
                                                        {testCallResults.coperato.message}
                                                    </p>
                                                )}
                                                {testCallResults.coperato.callId && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        Call ID: {testCallResults.coperato.callId}
                                                    </p>
                                                )}
                                                {testCallResults.coperato.extension && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        Extension: {testCallResults.coperato.extension}
                                                    </p>
                                                )}
                                                {/* Error Details */}
                                                {!testCallResults.coperato.success && testCallResults.coperato.details && (
                                                    <details className="mt-2 text-xs">
                                                        <summary className="cursor-pointer text-red-700 hover:text-red-900 font-medium">
                                                            View Error Details
                                                        </summary>
                                                        <div className="mt-2 space-y-1">
                                                            {testCallResults.coperato.endpoint && (
                                                                <p className="text-xs text-gray-600">
                                                                    <span className="font-medium">Endpoint:</span> {testCallResults.coperato.endpoint}
                                                                </p>
                                                            )}
                                                            {testCallResults.coperato.httpStatus && (
                                                                <p className="text-xs text-gray-600">
                                                                    <span className="font-medium">HTTP Status:</span> {testCallResults.coperato.httpStatus}
                                                                </p>
                                                            )}
                                                            <pre className="mt-2 p-2 bg-red-100 rounded text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                                                {JSON.stringify(testCallResults.coperato.details, null, 2)}
                                                            </pre>
                                                        </div>
                                                    </details>
                                                )}
                                                {/* Technical Details (Expandable) */}
                                                {testCallResults.coperato.success && testCallResults.coperato.fullResponse && (
                                                    <details className="mt-3 text-xs">
                                                        <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                                                            Technical Details
                                                        </summary>
                                                        <div className="mt-2 space-y-1">
                                                            {testCallResults.coperato.httpStatus && (
                                                                <p className="text-xs text-gray-600">
                                                                    HTTP Status: {testCallResults.coperato.httpStatus}
                                                                </p>
                                                            )}
                                                            {testCallResults.coperato.fullResponse && (
                                                                <details className="mt-2">
                                                                    <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                                                                        View Full API Response
                                                                    </summary>
                                                                    <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                                                        {JSON.stringify(testCallResults.coperato.fullResponse, null, 2)}
                                                                    </pre>
                                                                </details>
                                                            )}
                                                        </div>
                                                    </details>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}
                    </div>

                    {/* SquareTalk API Key */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 transition-colors">
                        <div 
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => toggleVoipSection('squaretalk')}
                        >
                            <div className="flex items-center space-x-3">
                                <div className="w-9 h-9 bg-violet-100 rounded-lg flex items-center justify-center">
                                    <Phone className="h-4 w-4 text-violet-600" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900">SquareTalk</h3>
                                    <p className="text-xs text-gray-500">Voice calling integration</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {(apiKeys.squaretalk?.enabled) && (
                                    <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Active</Badge>
                                )}
                                {expandedVoipSections.squaretalk ? (
                                    <ChevronUp className="h-4 w-4 text-gray-400" />
                                ) : (
                                    <ChevronDown className="h-4 w-4 text-gray-400" />
                                )}
                            </div>
                        </div>

                        {expandedVoipSections.squaretalk && (
                        <div className="p-6 pt-0 space-y-4 border-t">
                            <div>
                                <Label htmlFor="squaretalk-api-key">API Key</Label>
                                <div className="flex space-x-2">
                                    <Input
                                        id="squaretalk-api-key"
                                        type={showApiKeys.squaretalk ? "text" : "password"}
                                        value={apiKeys.squaretalk.apiKey}
                                        onChange={(e) => handleApiKeyChange("squaretalk", "apiKey", e.target.value)}
                                        placeholder="Enter SquareTalk API Key"
                                        className="flex-1"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            setShowApiKeys((prev) => ({ ...prev, squaretalk: !prev.squaretalk }))
                                        }
                                    >
                                        {showApiKeys.squaretalk ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </Button>
                                    {apiKeys.squaretalk.apiKey && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleClearApiKey("squaretalk")}
                                            className="text-red-600 hover:text-red-700"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="squaretalk-domain">Domain (Important for API access)</Label>
                                <Input
                                    id="squaretalk-domain"
                                    type="text"
                                    value={apiKeys.squaretalk?.domain || ""}
                                    onChange={(e) => handleApiKeyChange("squaretalk", "domain", e.target.value)}
                                    placeholder="Enter SquareTalk Domain or Custom API Domain"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Your SquareTalk custom domain or API domain. This is often required for API access. 
                                    Check your SquareTalk portal for your account's API domain. 
                                    If CloudFront errors occur, this field might be required.
                                </p>
                            </div>

                            <div>
                                <Label htmlFor="squaretalk-account-id">Account ID (Optional)</Label>
                                <Input
                                    id="squaretalk-account-id"
                                    type="text"
                                    value={apiKeys.squaretalk?.accountId || ""}
                                    onChange={(e) => handleApiKeyChange("squaretalk", "accountId", e.target.value)}
                                    placeholder="Enter SquareTalk Account ID (optional - API token may contain account info)"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Account ID may be optional if your API token contains account information. 
                                    If you get errors, try adding your Account ID from SquareTalk portal.
                                </p>
                            </div>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="squaretalk-enabled"
                                    checked={apiKeys.squaretalk?.enabled || false}
                                    onChange={(e) => handleApiKeyChange("squaretalk", "enabled", e.target.checked)}
                                    className="rounded"
                                />
                                <Label htmlFor="squaretalk-enabled">Enable SquareTalk Integration</Label>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleTestConnection("squaretalk")}
                                    disabled={testingProvider === "squaretalk" || !apiKeys.squaretalk.apiKey}
                                    className="flex items-center space-x-2"
                                >
                                    {testingProvider === "squaretalk" ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            <span>Testing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Phone className="h-4 w-4" />
                                            <span>Test Connection</span>
                                        </>
                                    )}
                                </Button>
                                {testResults.squaretalk && (
                                    <div className={`flex items-center space-x-2 ${testResults.squaretalk.success ? "text-green-600" : "text-red-600"}`}>
                                        {testResults.squaretalk.success ? (
                                            <CheckCircle className="h-4 w-4" />
                                        ) : (
                                            <XCircle className="h-4 w-4" />
                                        )}
                                        <span className="text-sm font-medium">{testResults.squaretalk.success ? "Connected" : "Failed"}</span>
                                    </div>
                                )}
                            </div>

                            {testResults.squaretalk && !testResults.squaretalk.success && (
                                <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
                                    <div>
                                    <p className="text-sm text-red-800 font-medium">Connection Failed</p>
                                        <p className="text-xs text-red-600 mt-1 whitespace-pre-line">{testResults.squaretalk.reason}</p>
                                </div>
                                    
                                    {testResults.squaretalk.troubleshooting && (
                                        <div className="mt-3 pt-3 border-t border-red-300">
                                            <p className="text-xs font-semibold text-red-900 mb-2">Technical Details:</p>
                                            <div className="text-xs text-red-700 space-y-1">
                                                {testResults.squaretalk.troubleshooting.endpoint && (
                                                    <p><span className="font-medium">Endpoint:</span> {testResults.squaretalk.troubleshooting.endpoint}</p>
                                                )}
                                                {testResults.squaretalk.troubleshooting.accountId && (
                                                    <p><span className="font-medium">Account ID:</span> {testResults.squaretalk.troubleshooting.accountId}</p>
                                                )}
                                                {testResults.squaretalk.troubleshooting.squaretalkError && (
                                                    <p><span className="font-medium">SquareTalk Error:</span> {testResults.squaretalk.troubleshooting.squaretalkError}</p>
                                                )}
                                                {testResults.squaretalk.troubleshooting.squaretalkErrorCode && (
                                                    <p><span className="font-medium">Error Code:</span> {testResults.squaretalk.troubleshooting.squaretalkErrorCode}</p>
                                                )}
                                                {testResults.squaretalk.troubleshooting.responseCode && (
                                                    <p><span className="font-medium">HTTP Status:</span> {testResults.squaretalk.troubleshooting.responseCode}</p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {testResults.squaretalk.details?.responseBody && (
                                        <div className="mt-3 pt-3 border-t border-red-300">
                                            <details className="text-xs">
                                                <summary className="cursor-pointer text-red-900 font-medium hover:text-red-700">
                                                    View Full API Response
                                                </summary>
                                                <pre className="mt-2 p-2 bg-red-100 rounded text-xs overflow-x-auto">
                                                    {JSON.stringify(testResults.squaretalk.details.responseBody, null, 2)}
                                                </pre>
                                            </details>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Test Call Section */}
                            <div className="mt-6 pt-4 border-t space-y-4">
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Test Call</h4>
                                    <p className="text-xs text-gray-500 mb-3">
                                        Test the SquareTalk integration by initiating a real call. Enter the agent extension and destination phone number.
                                    </p>
                                    <div className="space-y-3">
                                        <div>
                                            <Label htmlFor="test-agent-extension-squaretalk">Agent Extension</Label>
                                            <Input
                                                id="test-agent-extension-squaretalk"
                                                type="text"
                                                value={testCallInputs.agentExtension}
                                                onChange={(e) => setTestCallInputs((prev) => ({ ...prev, agentExtension: e.target.value }))}
                                                placeholder="Enter agent extension (e.g., 1001)"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="test-destination-phone-squaretalk">Destination Phone Number</Label>
                                            <Input
                                                id="test-destination-phone-squaretalk"
                                                type="tel"
                                                value={testCallInputs.destinationPhone}
                                                onChange={(e) => setTestCallInputs((prev) => ({ ...prev, destinationPhone: e.target.value }))}
                                                placeholder="Enter phone number (e.g., +1234567890)"
                                            />
                                        </div>
                                        <Button
                                            type="button"
                                            variant="default"
                                            onClick={() => handleTestCall("squaretalk")}
                                            disabled={testCallLoading || !testCallInputs.agentExtension || !testCallInputs.destinationPhone || !apiKeys.squaretalk.apiKey}
                                            className="w-full flex items-center justify-center space-x-2"
                                        >
                                            {testCallLoading ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    <span>Initiating Call...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Phone className="h-4 w-4" />
                                                    <span>Test Call</span>
                                                </>
                                            )}
                                        </Button>
                                        {testCallResults.squaretalk && (
                                            <div className={`p-3 rounded-lg border ${testCallResults.squaretalk.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                                                <div className="flex items-center space-x-2 mb-2">
                                                    {testCallResults.squaretalk.success ? (
                                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                                    ) : (
                                                        <XCircle className="h-4 w-4 text-red-600" />
                                                    )}
                                                    <span className={`text-sm font-medium ${testCallResults.squaretalk.success ? "text-green-800" : "text-red-800"}`}>
                                                        {testCallResults.squaretalk.success ? "Call Initiated Successfully" : "Call Failed"}
                                                    </span>
                                                </div>
                                                {testCallResults.squaretalk.message && (
                                                    <p className={`text-xs ${testCallResults.squaretalk.success ? "text-green-600" : "text-red-600"}`}>
                                                        {testCallResults.squaretalk.message}
                                                    </p>
                                                )}
                                                {testCallResults.squaretalk.note && (
                                                    <p className="text-xs text-blue-600 mt-1">
                                                        Note: {testCallResults.squaretalk.note}
                                                    </p>
                                                )}
                                                {testCallResults.squaretalk.extension && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        Extension: {testCallResults.squaretalk.extension}
                                                    </p>
                                                )}
                                                {/* Call Status Display - Auto-updates */}
                                                {testCallResults.squaretalk.success && (
                                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <p className="text-xs font-semibold text-gray-700">Call Status:</p>
                                                            {callStatusPolling.squaretalk && (
                                                                <Badge variant="outline" className="text-xs">
                                                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                                    Monitoring...
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className="space-y-2">
                                                            {/* Extension Status */}
                                                            {(testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) && (
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-xs text-gray-600">Extension Status:</span>
                                                                    <Badge 
                                                                        className={`text-xs font-semibold ${
                                                                            (testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) === "ringing" ? "bg-yellow-100 text-yellow-800 border-yellow-300 animate-pulse" :
                                                                            (testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) === "Connected" || callStatus.squaretalk?.isConnected ? "bg-green-100 text-green-800 border-green-300" :
                                                                            (testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) === "Busy" ? "bg-orange-100 text-orange-800 border-orange-300" :
                                                                            (testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) === "DISCONNECTED" ? "bg-red-100 text-red-800 border-red-300" :
                                                                            "bg-gray-100 text-gray-800 border-gray-300"
                                                                        }`}
                                                                    >
                                                                        {(testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) === "ringing" && "🔔 "}
                                                                        {(testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) === "Connected" && "✅ "}
                                                                        {(testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) === "DISCONNECTED" && "❌ "}
                                                                        {(testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus) === "Busy" && "📞 "}
                                                                        {testCallResults.squaretalk.extensionStatus || callStatus.squaretalk?.extensionStatus}
                                                                    </Badge>
                                                                </div>
                                                            )}
                                                            {/* Connectivity Status */}
                                                            {callStatus.squaretalk?.connectivity && (
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-xs text-gray-600">Connectivity:</span>
                                                                    <Badge 
                                                                        className={`text-xs ${
                                                                            callStatus.squaretalk.connectivity === "Connected" ? "bg-green-100 text-green-800 border-green-300" : "bg-gray-100 text-gray-800 border-gray-300"
                                                                        }`}
                                                                    >
                                                                        {callStatus.squaretalk.connectivity}
                                                                    </Badge>
                                                                </div>
                                                            )}
                                                            {/* Description */}
                                                            {(testCallResults.squaretalk.description || callStatus.squaretalk?.description) && (
                                                                <p className="text-xs text-gray-600 mt-1 italic bg-gray-50 p-2 rounded">
                                                                    {testCallResults.squaretalk.description || callStatus.squaretalk?.description}
                                                                </p>
                                                            )}
                                                            {/* Last Updated */}
                                                            {callStatus.squaretalk?.lastUpdated && (
                                                                <p className="text-xs text-gray-400 mt-1">
                                                                    Last updated: {new Date(callStatus.squaretalk.lastUpdated).toLocaleTimeString()}
                                                                </p>
                                                            )}
                                                            {/* Action Buttons */}
                                                            <div className="flex space-x-2 mt-2">
                                                                {/* End Call Button */}
                                                                {testCallResults.squaretalk.success && 
                                                                 (testCallResults.squaretalk.extensionStatus === "ringing" || 
                                                                  testCallResults.squaretalk.extensionStatus === "Connected" ||
                                                                  callStatus.squaretalk?.extensionStatus === "ringing" ||
                                                                  callStatus.squaretalk?.extensionStatus === "Connected" ||
                                                                  callStatus.squaretalk?.isConnected) && (
                                                                    <Button
                                                                        type="button"
                                                                        variant="destructive"
                                                                        size="sm"
                                                                        onClick={() => handleEndCall("squaretalk", testCallResults.squaretalk.extension || testCallInputs.agentExtension)}
                                                                        disabled={endingCall}
                                                                        className="flex-1 text-xs"
                                                                    >
                                                                        {endingCall ? (
                                                                            <>
                                                                                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                                                Ending...
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Phone className="h-3 w-3 mr-1" />
                                                                                End Call
                                                                            </>
                                                                        )}
                                                                    </Button>
                                                                )}
                                                                {/* Stop Monitoring Button */}
                                                                {callStatusPolling.squaretalk && (
                                                                    <Button
                                                                        type="button"
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={() => stopCallStatusPolling("squaretalk")}
                                                                        className="flex-1 text-xs"
                                                                    >
                                                                        Stop Monitoring
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {/* Technical Details (Expandable) */}
                                                        <details className="mt-3 text-xs">
                                                            <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                                                                Technical Details
                                                            </summary>
                                                            <div className="mt-2 space-y-1">
                                                                {testCallResults.squaretalk.httpStatus && (
                                                                    <p className="text-xs text-gray-600">
                                                                        HTTP Status: {testCallResults.squaretalk.httpStatus}
                                                                    </p>
                                                                )}
                                                                {testCallResults.squaretalk.fullResponse && (
                                                                    <details className="mt-2">
                                                                        <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                                                                            View Full API Response
                                                                        </summary>
                                                                        <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                                                            {JSON.stringify(testCallResults.squaretalk.fullResponse, null, 2)}
                                                                        </pre>
                                                                    </details>
                                                                )}
                                                            </div>
                                                        </details>
                                                    </div>
                                                )}
                                                {!testCallResults.squaretalk.success && testCallResults.squaretalk.details && (
                                                    <details className="mt-2 text-xs">
                                                        <summary className="cursor-pointer text-red-700 hover:text-red-900 font-medium">
                                                            View Error Details
                                                        </summary>
                                                        <div className="mt-2 space-y-1">
                                                            {testCallResults.squaretalk.endpoint && (
                                                                <p><span className="font-medium">Endpoint:</span> {testCallResults.squaretalk.endpoint}</p>
                                                            )}
                                                            {testCallResults.squaretalk.httpStatus && (
                                                                <p><span className="font-medium">HTTP Status:</span> {testCallResults.squaretalk.httpStatus}</p>
                                                            )}
                                                            {testCallResults.squaretalk.requestBody && (
                                                                <p><span className="font-medium">Request Body:</span> {JSON.stringify(testCallResults.squaretalk.requestBody)}</p>
                                                            )}
                                                            <pre className="mt-2 p-2 bg-red-100 rounded text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                                                {JSON.stringify(testCallResults.squaretalk.details, null, 2)}
                                                            </pre>
                                                        </div>
                                                    </details>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}
                    </div>

                    {/* CommPeak API Key  */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 transition-colors">
                        <div 
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => toggleVoipSection('commpeak')}
                        >
                            <div className="flex items-center space-x-3">
                                <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center">
                                    <Phone className="h-4 w-4 text-emerald-600" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900">CommPeak</h3>
                                    <p className="text-xs text-gray-500">Click-to-call integration</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {(apiKeys.commpeak?.enabled) && (
                                    <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Active</Badge>
                                )}
                                {expandedVoipSections.commpeak ? (
                                    <ChevronUp className="h-4 w-4 text-gray-400" />
                                ) : (
                                    <ChevronDown className="h-4 w-4 text-gray-400" />
                                )}
                            </div>
                        </div>

                        {expandedVoipSections.commpeak && (
                        <div className="p-6 pt-0 space-y-4 border-t">
                            <div>
                                <Label htmlFor="commpeak-crm-id">CRM ID *</Label>
                                <Input
                                    id="commpeak-crm-id"
                                    type="text"
                                    value={apiKeys.commpeak?.crmId || ""}
                                    onChange={(e) => handleApiKeyChange("commpeak", "crmId", e.target.value)}
                                    placeholder="Enter CRM vendor ID (e.g., 76)"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Unique CRM vendor ID (Static - provided by CommPeak). <strong>This is DIFFERENT from Client ID below.</strong> Both are required. If support gave you only one ID, ask them for both separately.
                                </p>
                            </div>
                            
                            <div>
                                <Label htmlFor="commpeak-client-id">Client ID *</Label>
                                <Input
                                    id="commpeak-client-id"
                                    type="text"
                                    value={apiKeys.commpeak?.clientId || apiKeys.commpeak?.c2cId || ""}
                                    onChange={(e) => {
                                        handleApiKeyChange("commpeak", "clientId", e.target.value);
                                        // Also update c2cId for backward compatibility
                                        if (!apiKeys.commpeak?.clientId && apiKeys.commpeak?.c2cId) {
                                            handleApiKeyChange("commpeak", "c2cId", e.target.value);
                                        }
                                    }}
                                    placeholder="Enter Click2Call Client ID (e.g., 1248)"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Unique Click2Call ID (Static - provided by CommPeak). Also called C2C ID. <strong>This is DIFFERENT from CRM ID above.</strong> Both are required. If support gave you only one ID, ask them for both separately.
                                </p>
                            </div>
                            
                            <div>
                                <Label htmlFor="commpeak-route-number">Route Number (Prefix)</Label>
                                <Input
                                    id="commpeak-route-number"
                                    type="text"
                                    value={apiKeys.commpeak?.routeNumber || apiKeys.commpeak?.prefix || "00"}
                                    onChange={(e) => {
                                        handleApiKeyChange("commpeak", "routeNumber", e.target.value);
                                        handleApiKeyChange("commpeak", "prefix", e.target.value);
                                    }}
                                    placeholder="00"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    PBX outbound calls prefix (default: '00'). Must match your dial pattern configuration on Manual Dialing Routes.
                                </p>
                            </div>
                            
                            <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
                                <p className="text-xs text-blue-800">
                                    <strong>Note:</strong> CommPeak Click2Call always uses the standard domain <strong>click2call.pbx.commpeak.com</strong>. 
                                    The endpoint format is: <code className="bg-blue-100 px-1 rounded">https://click2call.pbx.commpeak.com/call/{'{crm_id}'}/{'{client_id}'}/{'{leg_a}'}/{'{leg_b}'}/{'{prefix}'}</code>
                                </p>
                            </div>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="commpeak-enabled"
                                    checked={apiKeys.commpeak?.enabled || false}
                                    onChange={(e) => handleApiKeyChange("commpeak", "enabled", e.target.checked)}
                                    className="rounded"
                                />
                                <Label htmlFor="commpeak-enabled">Enable CommPeak Integration</Label>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleTestConnection("commpeak")}
                                    disabled={testingProvider === "commpeak" || !apiKeys.commpeak?.crmId || (!apiKeys.commpeak?.clientId && !apiKeys.commpeak?.c2cId)}
                                    className="flex items-center space-x-2"
                                >
                                    {testingProvider === "commpeak" ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            <span>Testing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Phone className="h-4 w-4" />
                                            <span>Test Connection</span>
                                        </>
                                    )}
                                </Button>
                                {testResults.commpeak && (
                                    <div className={`flex items-center space-x-2 ${testResults.commpeak.success ? "text-green-600" : "text-red-600"}`}>
                                        {testResults.commpeak.success ? (
                                            <CheckCircle className="h-4 w-4" />
                                        ) : (
                                            <XCircle className="h-4 w-4" />
                                        )}
                                        <span className="text-sm font-medium">{testResults.commpeak.success ? "Connected" : "Failed"}</span>
                                    </div>
                                )}
                            </div>

                            {testResults.commpeak && !testResults.commpeak.success && testResults.commpeak.reason && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-800 font-medium">Connection Failed</p>
                                    <p className="text-xs text-red-600 mt-1">{testResults.commpeak.reason}</p>
                                    {testResults.commpeak.troubleshooting && (
                                        <details className="mt-2 text-xs">
                                            <summary className="cursor-pointer text-red-700 hover:text-red-900 font-medium">
                                                View Troubleshooting
                                            </summary>
                                            <div className="mt-2 space-y-1 p-2 bg-red-100 rounded">
                                                {testResults.commpeak.troubleshooting.suggestion && (
                                                    <p className="text-xs text-red-800">
                                                        <span className="font-medium">Suggestion:</span> {testResults.commpeak.troubleshooting.suggestion}
                                                    </p>
                                                )}
                                                {testResults.commpeak.troubleshooting.action && (
                                                    <p className="text-xs text-red-800 mt-1">
                                                        <span className="font-medium">Action:</span> {testResults.commpeak.troubleshooting.action}
                                                    </p>
                                                )}
                                                {testResults.commpeak.troubleshooting.errorCode && (
                                                    <p className="text-xs text-red-800 mt-1">
                                                        <span className="font-medium">Error Code:</span> {testResults.commpeak.troubleshooting.errorCode}
                                                    </p>
                                                )}
                                </div>
                                        </details>
                                    )}
                                </div>
                            )}

                            {/* Test Call Section for CommPeak */}
                            <div className="mt-6 pt-4 border-t space-y-4">
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Test Call</h4>
                                    <p className="text-xs text-gray-500 mb-3">
                                        Test the CommPeak integration by initiating a real call. Enter the agent extension and destination phone number.
                                    </p>
                                    <div className="space-y-3">
                                        <div>
                                            <Label htmlFor="test-agent-extension-commpeak">Agent Extension</Label>
                                            <Input
                                                id="test-agent-extension-commpeak"
                                                type="text"
                                                value={testCallInputs.agentExtension}
                                                onChange={(e) => setTestCallInputs((prev) => ({ ...prev, agentExtension: e.target.value }))}
                                                placeholder="Enter agent extension (e.g., 1001)"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="test-destination-phone-commpeak">Destination Phone Number</Label>
                                            <Input
                                                id="test-destination-phone-commpeak"
                                                type="tel"
                                                value={testCallInputs.destinationPhone}
                                                onChange={(e) => setTestCallInputs((prev) => ({ ...prev, destinationPhone: e.target.value }))}
                                                placeholder="Enter phone number (e.g., +1234567890)"
                                            />
                                        </div>
                                        <Button
                                            type="button"
                                            variant="default"
                                            onClick={() => handleTestCall("commpeak")}
                                            disabled={testCallLoading || !testCallInputs.agentExtension || !testCallInputs.destinationPhone || !apiKeys.commpeak?.crmId || (!apiKeys.commpeak?.clientId && !apiKeys.commpeak?.c2cId)}
                                            className="w-full flex items-center justify-center space-x-2"
                                        >
                                            {testCallLoading ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    <span>Initiating Call...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Phone className="h-4 w-4" />
                                                    <span>Test Call</span>
                                                </>
                                            )}
                                        </Button>
                                        {testCallResults.commpeak && (
                                            <div className={`p-3 rounded-lg border ${testCallResults.commpeak.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                                                <div className="flex items-center space-x-2 mb-2">
                                                    {testCallResults.commpeak.success ? (
                                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                                    ) : (
                                                        <XCircle className="h-4 w-4 text-red-600" />
                                                    )}
                                                    <span className={`text-sm font-medium ${testCallResults.commpeak.success ? "text-green-800" : "text-red-800"}`}>
                                                        {testCallResults.commpeak.success ? "Call Initiated Successfully" : "Call Failed"}
                                                    </span>
                                                </div>
                                                {testCallResults.commpeak.message && (
                                                    <p className={`text-xs ${testCallResults.commpeak.success ? "text-green-600" : "text-red-600"}`}>
                                                        {testCallResults.commpeak.message}
                                                    </p>
                                                )}
                                                {testCallResults.commpeak.callId && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        Call ID: {testCallResults.commpeak.callId}
                                                    </p>
                                                )}
                                                {testCallResults.commpeak.extension && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        Extension: {testCallResults.commpeak.extension}
                                                    </p>
                                                )}
                                                {/* Error Details */}
                                                {!testCallResults.commpeak.success && testCallResults.commpeak.details && (
                                                    <details className="mt-2 text-xs">
                                                        <summary className="cursor-pointer text-red-700 hover:text-red-900 font-medium">
                                                            View Error Details
                                                        </summary>
                                                        <div className="mt-2 space-y-1">
                                                            {testCallResults.commpeak.details.errorCode && (
                                                                <p className="text-xs text-red-800">
                                                                    <span className="font-medium">Error Code:</span> {testCallResults.commpeak.details.errorCode}
                                                                </p>
                                                            )}
                                                            {testCallResults.commpeak.details.troubleshooting && (
                                                                <div className="mt-2 p-2 bg-red-100 rounded">
                                                                    {testCallResults.commpeak.details.troubleshooting.suggestion && (
                                                                        <p className="text-xs text-red-800">
                                                                            <span className="font-medium">Suggestion:</span> {testCallResults.commpeak.details.troubleshooting.suggestion}
                                                                        </p>
                                                                    )}
                                                                    {testCallResults.commpeak.details.troubleshooting.action && (
                                                                        <p className="text-xs text-red-800 mt-1">
                                                                            <span className="font-medium">Action:</span> {testCallResults.commpeak.details.troubleshooting.action}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {testCallResults.commpeak.endpoint && (
                                                                <p className="text-xs text-gray-600 mt-1">
                                                                    <span className="font-medium">Endpoint:</span> {testCallResults.commpeak.endpoint}
                                                                </p>
                                                            )}
                                                            {testCallResults.commpeak.httpStatus && (
                                                                <p className="text-xs text-gray-600">
                                                                    <span className="font-medium">HTTP Status:</span> {testCallResults.commpeak.httpStatus}
                                                                </p>
                                                            )}
                                                            <pre className="mt-2 p-2 bg-red-100 rounded text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                                                {JSON.stringify(testCallResults.commpeak.details, null, 2)}
                                                            </pre>
                                                        </div>
                                                    </details>
                                                )}
                                                {/* Technical Details (Expandable) */}
                                                {testCallResults.commpeak.success && testCallResults.commpeak.fullResponse && (
                                                    <details className="mt-3 text-xs">
                                                        <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                                                            Technical Details
                                                        </summary>
                                                        <div className="mt-2 space-y-1">
                                                            {testCallResults.commpeak.httpStatus && (
                                                                <p className="text-xs text-gray-600">
                                                                    HTTP Status: {testCallResults.commpeak.httpStatus}
                                                                </p>
                                                            )}
                                                            {testCallResults.commpeak.fullResponse && (
                                                                <details className="mt-2">
                                                                    <summary className="cursor-pointer text-gray-600 hover:text-gray-800 font-medium">
                                                                        View Full API Response
                                                                    </summary>
                                                                    <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                                                        {JSON.stringify(testCallResults.commpeak.fullResponse, null, 2)}
                                                                    </pre>
                                                                </details>
                                                            )}
                                                        </div>
                                                    </details>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}
                    </div>

                    {/* Octella API Key */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 transition-colors">
                        <div
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => toggleVoipSection('octella')}
                        >
                            <div className="flex items-center space-x-3">
                                <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center">
                                    <Phone className="h-4 w-4 text-teal-600" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900">Octella</h3>
                                    <p className="text-xs text-gray-500">Click2Call via SIP line</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {apiKeys.octella?.enabled && (
                                    <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Active</Badge>
                                )}
                                {expandedVoipSections.octella ? (
                                    <ChevronUp className="h-4 w-4 text-gray-400" />
                                ) : (
                                    <ChevronDown className="h-4 w-4 text-gray-400" />
                                )}
                            </div>
                        </div>

                        {expandedVoipSections.octella && (
                        <div className="p-6 pt-0 space-y-4 border-t">
                            <div>
                                <Label htmlFor="octella-api-key">API Key (Access Token)</Label>
                                <div className="relative">
                                    <Input
                                        id="octella-api-key"
                                        type={showApiKeys.octella ? "text" : "password"}
                                        value={apiKeys.octella?.apiKey || ""}
                                        onChange={(e) => handleApiKeyChange("octella", "apiKey", e.target.value)}
                                        placeholder="Enter Octella access token"
                                        className="flex-1 pr-20"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        onClick={() => setShowApiKeys((prev) => ({ ...prev, octella: !prev.octella }))}
                                    >
                                        {showApiKeys.octella ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                    {apiKeys.octella?.apiKey && (
                                        <button
                                            type="button"
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-600"
                                            onClick={() => handleClearApiKey("octella")}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="octella-number-group">Virtual Number Group</Label>
                                <Input
                                    id="octella-number-group"
                                    type="text"
                                    value={apiKeys.octella?.virtualNumberGroup || ""}
                                    onChange={(e) => handleApiKeyChange("octella", "virtualNumberGroup", e.target.value)}
                                    placeholder="e.g. 123 or g123"
                                    className="flex-1"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Group ID from Octella (web interface shows it as <span className="font-mono">g###</span>). Get it via the Data API method <span className="font-mono">get.virtual_number_groups</span>.
                                </p>
                            </div>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="octella-enabled"
                                    checked={apiKeys.octella?.enabled || false}
                                    onChange={(e) => handleApiKeyChange("octella", "enabled", e.target.checked)}
                                    className="rounded"
                                />
                                <Label htmlFor="octella-enabled">Enable Octella Integration</Label>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleTestConnection("octella")}
                                    disabled={testingProvider === "octella" || !apiKeys.octella?.apiKey}
                                    className="flex items-center space-x-2"
                                >
                                    {testingProvider === "octella" ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            <span>Testing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Phone className="h-4 w-4" />
                                            <span>Test Connection</span>
                                        </>
                                    )}
                                </Button>
                                {testResults.octella && (
                                    <div className={`flex items-center space-x-2 ${testResults.octella.success ? "text-green-600" : "text-red-600"}`}>
                                        {testResults.octella.success ? (
                                            <CheckCircle className="h-4 w-4" />
                                        ) : (
                                            <XCircle className="h-4 w-4" />
                                        )}
                                        <span className="text-sm font-medium">{testResults.octella.success ? "Connected" : "Failed"}</span>
                                    </div>
                                )}
                            </div>

                            {testResults.octella && !testResults.octella.success && testResults.octella.reason && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-800 font-medium">Connection Failed</p>
                                    <p className="text-xs text-red-600 mt-1">{testResults.octella.reason}</p>
                                </div>
                            )}

                            {/* Test Call Section for Octella */}
                            <div className="mt-6 pt-4 border-t space-y-4">
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Test Call</h4>
                                    <p className="text-xs text-gray-500 mb-3">
                                        Test the Octella integration by initiating a real call. Enter the agent's SIP line number (or employee ID) and the destination phone number.
                                    </p>
                                    <div className="space-y-3">
                                        <div>
                                            <Label htmlFor="test-extension-octella">Agent SIP Line / Employee ID</Label>
                                            <Input
                                                id="test-extension-octella"
                                                type="text"
                                                value={testCallInputs.agentExtension}
                                                onChange={(e) => setTestCallInputs((prev) => ({ ...prev, agentExtension: e.target.value }))}
                                                placeholder="e.g. 0123456"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="test-destination-phone-octella">Destination Phone Number</Label>
                                            <Input
                                                id="test-destination-phone-octella"
                                                type="tel"
                                                value={testCallInputs.destinationPhone}
                                                onChange={(e) => setTestCallInputs((prev) => ({ ...prev, destinationPhone: e.target.value }))}
                                                placeholder="Enter phone number (e.g., +1234567890)"
                                            />
                                        </div>
                                        <Button
                                            type="button"
                                            variant="default"
                                            onClick={() => handleTestCall("octella")}
                                            disabled={testCallLoading || !testCallInputs.agentExtension || !testCallInputs.destinationPhone || !apiKeys.octella?.apiKey || !apiKeys.octella?.virtualNumberGroup}
                                            className="w-full flex items-center justify-center space-x-2"
                                        >
                                            {testCallLoading ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    <span>Initiating Call...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Phone className="h-4 w-4" />
                                                    <span>Test Call</span>
                                                </>
                                            )}
                                        </Button>
                                        {testCallResults.octella && (
                                            <div className={`p-3 rounded-lg border ${testCallResults.octella.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                                                <div className="flex items-center space-x-2 mb-2">
                                                    {testCallResults.octella.success ? (
                                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                                    ) : (
                                                        <XCircle className="h-4 w-4 text-red-600" />
                                                    )}
                                                    <span className={`text-sm font-medium ${testCallResults.octella.success ? "text-green-800" : "text-red-800"}`}>
                                                        {testCallResults.octella.success ? "Call Initiated Successfully" : "Call Failed"}
                                                    </span>
                                                </div>
                                                {testCallResults.octella.message && (
                                                    <p className={`text-xs ${testCallResults.octella.success ? "text-green-600" : "text-red-600"}`}>
                                                        {testCallResults.octella.message}
                                                    </p>
                                                )}
                                                {testCallResults.octella.callId && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        Call Session ID: {testCallResults.octella.callId}
                                                    </p>
                                                )}
                                                {!testCallResults.octella.success && testCallResults.octella.details && (
                                                    <details className="mt-2 text-xs">
                                                        <summary className="cursor-pointer text-red-700 hover:text-red-900 font-medium">
                                                            View Error Details
                                                        </summary>
                                                        <pre className="mt-2 p-2 bg-red-100 rounded text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                                            {JSON.stringify(testCallResults.octella.details, null, 2)}
                                                        </pre>
                                                    </details>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}
                    </div>

                    {/* MMD Call Center Connect API Key */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 transition-colors">
                        <div
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => toggleVoipSection('ccc')}
                        >
                            <div className="flex items-center space-x-3">
                                <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center">
                                    <Phone className="h-4 w-4 text-indigo-600" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900">MMD Call Center Connect</h3>
                                    <p className="text-xs text-gray-500">Click to call via MMDSmart CCC</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {apiKeys.ccc?.enabled && (
                                    <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Active</Badge>
                                )}
                                {expandedVoipSections.ccc ? (
                                    <ChevronUp className="h-4 w-4 text-gray-400" />
                                ) : (
                                    <ChevronDown className="h-4 w-4 text-gray-400" />
                                )}
                            </div>
                        </div>

                        {expandedVoipSections.ccc && (
                        <div className="p-6 pt-0 space-y-4 border-t">
                            <div>
                                <Label htmlFor="ccc-api-key">API Key (Bearer Token)</Label>
                                <div className="relative">
                                    <Input
                                        id="ccc-api-key"
                                        type={showApiKeys.ccc ? "text" : "password"}
                                        value={apiKeys.ccc?.apiKey || ""}
                                        onChange={(e) => handleApiKeyChange("ccc", "apiKey", e.target.value)}
                                        placeholder="Enter CCC integration API key"
                                        className="flex-1 pr-20"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        onClick={() => setShowApiKeys((prev) => ({ ...prev, ccc: !prev.ccc }))}
                                    >
                                        {showApiKeys.ccc ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                    {apiKeys.ccc?.apiKey && (
                                        <button
                                            type="button"
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-600"
                                            onClick={() => handleClearApiKey("ccc")}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Generated once per organization under <span className="font-mono">My Company → API KEYS</span> (contact MMDSmart support to enable).
                                </p>
                            </div>

                            <div>
                                <Label htmlFor="ccc-domain">Server Domain</Label>
                                <Input
                                    id="ccc-domain"
                                    type="text"
                                    value={apiKeys.ccc?.domain || ""}
                                    onChange={(e) => handleApiKeyChange("ccc", "domain", e.target.value)}
                                    placeholder="e.g. ccc.example.com"
                                    className="flex-1"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    CCC server host. Calls are sent to <span className="font-mono">https://&lt;domain&gt;/api/ctc</span>.
                                </p>
                            </div>

                            <div>
                                <Label htmlFor="ccc-caller-id">Caller ID (optional)</Label>
                                <Input
                                    id="ccc-caller-id"
                                    type="text"
                                    value={apiKeys.ccc?.callerId || ""}
                                    onChange={(e) => handleApiKeyChange("ccc", "callerId", e.target.value)}
                                    placeholder="DID number shown to the lead"
                                    className="flex-1"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Must match a DID number assigned to your organization if provided.
                                </p>
                            </div>

                            <div>
                                <Label htmlFor="ccc-webhook-url">Webhook URL (optional)</Label>
                                <Input
                                    id="ccc-webhook-url"
                                    type="text"
                                    value={apiKeys.ccc?.webhookUrl || ""}
                                    onChange={(e) => handleApiKeyChange("ccc", "webhookUrl", e.target.value)}
                                    placeholder="https://your-crm.com/webhooks/ccc"
                                    className="flex-1"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Receives async call lifecycle events (setup, connect, terminate, recording_ready).
                                </p>
                            </div>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="ccc-enabled"
                                    checked={apiKeys.ccc?.enabled || false}
                                    onChange={(e) => handleApiKeyChange("ccc", "enabled", e.target.checked)}
                                    className="rounded"
                                />
                                <Label htmlFor="ccc-enabled">Enable Call Center Connect Integration</Label>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleTestConnection("ccc")}
                                    disabled={testingProvider === "ccc" || !apiKeys.ccc?.apiKey || !apiKeys.ccc?.domain}
                                    className="flex items-center space-x-2"
                                >
                                    {testingProvider === "ccc" ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            <span>Testing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Phone className="h-4 w-4" />
                                            <span>Test Connection</span>
                                        </>
                                    )}
                                </Button>
                                {testResults.ccc && (
                                    <div className={`flex items-center space-x-2 ${testResults.ccc.success ? "text-green-600" : "text-red-600"}`}>
                                        {testResults.ccc.success ? (
                                            <CheckCircle className="h-4 w-4" />
                                        ) : (
                                            <XCircle className="h-4 w-4" />
                                        )}
                                        <span className="text-sm font-medium">{testResults.ccc.success ? "Valid" : "Failed"}</span>
                                    </div>
                                )}
                            </div>

                            {testResults.ccc && !testResults.ccc.success && testResults.ccc.reason && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-800 font-medium">Validation Failed</p>
                                    <p className="text-xs text-red-600 mt-1">{testResults.ccc.reason}</p>
                                </div>
                            )}

                            {/* Test Call Section for CCC */}
                            <div className="mt-6 pt-4 border-t space-y-4">
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Test Call</h4>
                                    <p className="text-xs text-gray-500 mb-3">
                                        Test the integration by initiating a real call. Enter the agent's CCC username and the destination phone number.
                                    </p>
                                    <div className="space-y-3">
                                        <div>
                                            <Label htmlFor="test-extension-ccc">Agent Username</Label>
                                            <Input
                                                id="test-extension-ccc"
                                                type="text"
                                                value={testCallInputs.agentExtension}
                                                onChange={(e) => setTestCallInputs((prev) => ({ ...prev, agentExtension: e.target.value }))}
                                                placeholder="e.g. agent.jsmith"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="test-destination-phone-ccc">Destination Phone Number</Label>
                                            <Input
                                                id="test-destination-phone-ccc"
                                                type="tel"
                                                value={testCallInputs.destinationPhone}
                                                onChange={(e) => setTestCallInputs((prev) => ({ ...prev, destinationPhone: e.target.value }))}
                                                placeholder="Enter phone number (e.g., +1234567890)"
                                            />
                                        </div>
                                        <Button
                                            type="button"
                                            variant="default"
                                            onClick={() => handleTestCall("ccc")}
                                            disabled={testCallLoading || !testCallInputs.agentExtension || !testCallInputs.destinationPhone || !apiKeys.ccc?.apiKey || !apiKeys.ccc?.domain}
                                            className="w-full flex items-center justify-center space-x-2"
                                        >
                                            {testCallLoading ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    <span>Initiating Call...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Phone className="h-4 w-4" />
                                                    <span>Test Call</span>
                                                </>
                                            )}
                                        </Button>
                                        {testCallResults.ccc && (
                                            <div className={`p-3 rounded-lg border ${testCallResults.ccc.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                                                <div className="flex items-center space-x-2 mb-2">
                                                    {testCallResults.ccc.success ? (
                                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                                    ) : (
                                                        <XCircle className="h-4 w-4 text-red-600" />
                                                    )}
                                                    <span className={`text-sm font-medium ${testCallResults.ccc.success ? "text-green-800" : "text-red-800"}`}>
                                                        {testCallResults.ccc.success ? "Call Initiated Successfully" : "Call Failed"}
                                                    </span>
                                                </div>
                                                {testCallResults.ccc.message && (
                                                    <p className={`text-xs ${testCallResults.ccc.success ? "text-green-600" : "text-red-600"}`}>
                                                        {testCallResults.ccc.message}
                                                    </p>
                                                )}
                                                {testCallResults.ccc.callId && (
                                                    <p className="text-xs text-gray-600 mt-1">
                                                        Call ID: {testCallResults.ccc.callId}
                                                    </p>
                                                )}
                                                {!testCallResults.ccc.success && testCallResults.ccc.details && (
                                                    <details className="mt-2 text-xs">
                                                        <summary className="cursor-pointer text-red-700 hover:text-red-900 font-medium">
                                                            View Error Details
                                                        </summary>
                                                        <pre className="mt-2 p-2 bg-red-100 rounded text-xs overflow-x-auto max-h-40 overflow-y-auto">
                                                            {JSON.stringify(testCallResults.ccc.details, null, 2)}
                                                        </pre>
                                                    </details>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}
                    </div>

                    {/* Save Button */}
                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                        {hasUnsavedChanges() && (
                            <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-xs font-medium">
                                Unsaved Changes
                            </Badge>
                        )}
                        <Button
                            onClick={handleSaveAPIKeys}
                            disabled={loading || !hasUnsavedChanges()}
                            className="bg-indigo-600 hover:bg-indigo-700"
                        >
                            <Save className="h-4 w-4 mr-2" />
                            Save VOIP Settings
                        </Button>
                    </div>
                </div>
            </div>

            {/* AI Assistant Section */}
            <div className="mb-6 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div
                    className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white"
                    onClick={() => setExpandedAiSection(!expandedAiSection)}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <Sparkles className="h-4 w-4 text-purple-600" />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">AI Assistant (ChatGPT)</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                                {aiConfig.enabled
                                    ? `Active • model: ${aiConfig.model}${aiConfig.hasApiKey ? " • key configured" : " • no API key"}`
                                    : "Disabled"}
                            </p>
                        </div>
                    </div>
                    {expandedAiSection ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </div>

                {expandedAiSection && (
                    <div className="p-5 space-y-4">
                        {/* Enable toggle */}
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                                <Label className="text-sm font-medium text-gray-900">Enable AI Assistant</Label>
                                <p className="text-xs text-gray-500 mt-0.5">Allow users to chat with ChatGPT from a dedicated page</p>
                            </div>
                            <Switch
                                checked={aiConfig.enabled}
                                onCheckedChange={(v) => setAiConfig({ ...aiConfig, enabled: v })}
                            />
                        </div>

                        {/* API Key */}
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">OpenAI API Key</Label>
                            <div className="relative mt-1">
                                <Input
                                    type={showAiKey ? "text" : "password"}
                                    value={aiApiKeyInput}
                                    onChange={(e) => setAiApiKeyInput(e.target.value)}
                                    placeholder={aiConfig.hasApiKey ? `${aiConfig.keyPrefix}…  (leave blank to keep current)` : "sk-..."}
                                    className="pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowAiKey(!showAiKey)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showAiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            <p className="text-[11px] text-gray-500 mt-1">
                                Get a key at <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">platform.openai.com/api-keys</a>. Stored encrypted (AES-256-GCM).
                            </p>
                        </div>

                        {/* Model */}
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Model</Label>
                            <Select
                                options={[
                                    { label: "gpt-5.4  (recommended — Aug 2025 knowledge) — $2.50/$15 per 1M", value: "gpt-5.4" },
                                    { label: "gpt-5.4-mini  (cheaper, Aug 2025 knowledge) — $0.75/$4.50 per 1M", value: "gpt-5.4-mini" },
                                    { label: "gpt-5.5  (newest, may not be available on all accounts yet — Dec 2025) — $5/$30", value: "gpt-5.5" },
                                    { label: "gpt-5.5-pro  (top tier, requires tier 4+ account) — $30/$180", value: "gpt-5.5-pro" },
                                    { label: "gpt-4o  (older, June 2024 knowledge) — $2.50/$10 per 1M", value: "gpt-4o" },
                                    { label: "gpt-4o-mini  (cheapest, June 2024 knowledge) — $0.15/$0.60 per 1M", value: "gpt-4o-mini" }
                                ]}
                                value={aiConfig.model}
                                onChange={(value) => setAiConfig({ ...aiConfig, model: value })}
                                placeholder="Select model"
                            />
                        </div>

                        {/* System prompt */}
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">System Prompt (optional)</Label>
                            <textarea
                                value={aiConfig.systemPrompt}
                                onChange={(e) => setAiConfig({ ...aiConfig, systemPrompt: e.target.value })}
                                placeholder="e.g. You are a sales assistant for an online trading CRM. Help agents handle objections, summarize notes, and draft polite follow-up messages."
                                rows={3}
                                className="w-full mt-1 px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                            <p className="text-[11px] text-gray-500 mt-1">Sent to ChatGPT before every conversation to shape its behavior.</p>
                        </div>

                        {/* Token limit */}
                        <div>
                            <Label className="text-xs font-semibold text-gray-700">Monthly Token Limit per User</Label>
                            <Input
                                type="number"
                                min="0"
                                value={aiConfig.monthlyTokenLimitPerUser}
                                onChange={(e) => setAiConfig({ ...aiConfig, monthlyTokenLimitPerUser: parseInt(e.target.value || "0", 10) })}
                                placeholder="0 = unlimited"
                                className="mt-1"
                            />
                            <p className="text-[11px] text-gray-500 mt-1">
                                Cap monthly tokens per user to control costs. 0 = unlimited. (Reference: 500,000 tokens ≈ $0.10 on gpt-4o-mini)
                            </p>
                        </div>

                        <div className="flex justify-end pt-3 border-t border-gray-100">
                            <Button
                                onClick={handleSaveAiConfig}
                                disabled={aiSaving}
                                className="bg-purple-600 hover:bg-purple-700 text-white"
                            >
                                {aiSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                                Save AI Settings
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* AI Caller Integrations (RetellAI, MMD SMS, SMTP) */}
            <AiCallerIntegrations />

            {/* Affiliate API Keys Section */}
            <div className="mb-6 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div 
                    className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white"
                    onClick={() => setExpandedAffiliateSection(!expandedAffiliateSection)}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <Key className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">Affiliate API Keys</h3>
                            <p className="text-xs text-gray-500">Manage API access for affiliate lead submissions</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-green-600 border-green-200 text-xs">
                            {affiliateApiKeys.length} {affiliateApiKeys.length === 1 ? 'Key' : 'Keys'}
                        </Badge>
                        {expandedAffiliateSection ? (
                            <ChevronUp className="h-4 w-4 text-gray-400" />
                        ) : (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                        )}
                    </div>
                </div>

                {expandedAffiliateSection && (
                    <div className="p-5 space-y-5">
                        {/* Create API Key Button */}
                        <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-500">
                                Create and manage API keys for affiliate integrations
                            </p>
                            <Button
                                onClick={() => {
                                    setNewApiKeyData({ name: "", defaultStatus: "New", isActive: true, routingMode: "none", routingDeskId: "", emailTemplateId: "" });
                                    setShowCreateApiKeyModal(true);
                                }}
                                className="bg-green-600 hover:bg-green-700"
                                size="sm"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Create API Key
                            </Button>
                        </div>

                        {/* API Keys Table */}
                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-50/80">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">API Key</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Default Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Leads</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Last Used</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 bg-white">
                                    {affiliateApiKeys.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="px-4 py-10 text-center">
                                                <div className="flex flex-col items-center justify-center">
                                                    <div className="p-3 bg-gray-50 rounded-full mb-3">
                                                        <Key className="h-8 w-8 text-gray-300" />
                                                    </div>
                                                    <p className="text-sm font-medium text-gray-600">No API keys created yet</p>
                                                    <p className="text-xs text-gray-400 mt-1">Click "Create API Key" to get started</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        affiliateApiKeys.map((key) => (
                                            <tr key={key._id} className="hover:bg-gray-50/80 transition-colors">
                                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{key.name}</td>
                                                <td className="px-4 py-3 text-sm font-mono text-gray-600">
                                                    {key.keyPrefix}••••••••
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    <div className="flex flex-col gap-1">
                                                        <Badge variant="outline" className="text-xs w-fit">{key.defaultStatus}</Badge>
                                                        {key.routingMode === "desk" && (
                                                            <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-[10px] w-fit">
                                                                <ArrowRight className="h-2.5 w-2.5 mr-1" />
                                                                Desk: {availableDesks.find(d => String(d._id) === String(key.routingDeskId))?.name || "?"}
                                                            </Badge>
                                                        )}
                                                        {key.routingMode === "agents" && (
                                                            <Badge className="bg-purple-50 text-purple-700 border border-purple-200 text-[10px] w-fit">
                                                                <Users className="h-2.5 w-2.5 mr-1" />
                                                                Round-robin: {availableDesks.find(d => String(d._id) === String(key.routingDeskId))?.name || "?"}
                                                            </Badge>
                                                        )}
                                                        {key.emailTemplateId && (
                                                            <Badge className="bg-red-50 text-red-700 border border-red-200 text-[10px] w-fit">
                                                                <Mail className="h-2.5 w-2.5 mr-1" />
                                                                {emailTemplates.find((t) => String(t._id) === String(key.emailTemplateId))?.name || "Welcome email"}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    {key.isActive ? (
                                                        <Badge className="bg-green-100 text-green-800 text-xs">Active</Badge>
                                                    ) : (
                                                        <Badge className="bg-gray-100 text-gray-800 text-xs">Inactive</Badge>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700">{key.leadCount || 0}</td>
                                                <td className="px-4 py-3 text-sm text-gray-500">
                                                    {key.lastUsedAt
                                                        ? new Date(key.lastUsedAt).toLocaleDateString()
                                                        : "Never"}
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    <div className="flex items-center space-x-1">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => openEditApiKey(key)}
                                                            disabled={apiKeyLoading}
                                                            title="Edit (status, routing)"
                                                        >
                                                            <Pencil className="h-3 w-3" />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => handleUpdateApiKey(key._id, { isActive: !key.isActive })}
                                                            disabled={apiKeyLoading}
                                                            className="text-xs"
                                                        >
                                                            {key.isActive ? "Deactivate" : "Activate"}
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => handleRegenerateApiKey(key._id)}
                                                            disabled={apiKeyLoading}
                                                            title="Regenerate API Key"
                                                        >
                                                            <RefreshCw className="h-3 w-3" />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => handleDeleteApiKey(key._id)}
                                                            disabled={apiKeyLoading}
                                                            className="text-red-600 hover:text-red-700"
                                                            title="Delete API Key"
                                                        >
                                                            <Trash2 className="h-3 w-3" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Notification Settings - Only show if there are API keys */}
                        {affiliateApiKeys.length > 0 && (
                            <div className="mt-5 pt-5 border-t border-gray-100">
                                <div className="mb-4">
                                    <div className="flex items-center gap-2.5 mb-1.5">
                                        <div className="p-1.5 bg-orange-100 rounded-lg">
                                            <Bell className="h-3.5 w-3.5 text-orange-600" />
                                        </div>
                                        <h4 className="text-sm font-semibold text-gray-900">Notification Settings</h4>
                                    </div>
                                    <p className="text-xs text-gray-500 ml-8">
                                        Control which affiliates send notifications when new leads are received.
                                    </p>
                                </div>
                                
                                <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 bg-white overflow-hidden">
                                    {affiliateApiKeys.map((key) => {
                                        const isMuted = mutedAffiliates.includes(key.name);
                                        return (
                                            <div key={key._id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                                                <div className="flex items-center space-x-3">
                                                    <div className={`p-2 rounded-lg ${isMuted ? 'bg-gray-100' : 'bg-orange-50'}`}>
                                                        {isMuted ? (
                                                            <BellOff className="h-4 w-4 text-gray-400" />
                                                        ) : (
                                                            <Bell className="h-4 w-4 text-orange-600" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">{key.name}</p>
                                                        <p className="text-xs text-gray-500">
                                                            {isMuted ? "Notifications muted" : "Notifications enabled"}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-3">
                                                    {isMuted && (
                                                        <Badge variant="outline" className="text-gray-600 border-gray-300 text-xs">
                                                            Muted
                                                        </Badge>
                                                    )}
                                                    <Switch
                                                        checked={!isMuted}
                                                        onCheckedChange={() => handleToggleAffiliateNotification(key.name)}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="flex justify-end">
                                    <Button
                                        onClick={handleSaveNotificationSettings}
                                        disabled={loading || JSON.stringify(mutedAffiliates.sort()) === JSON.stringify(originalMutedAffiliates.sort())}
                                        className="bg-orange-600 hover:bg-orange-700"
                                        size="sm"
                                    >
                                        <Save className="h-4 w-4 mr-2" />
                                        Save Notification Settings
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* API Documentation Info */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100">
                            <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                                <Globe className="h-4 w-4 text-blue-600" />
                                API Endpoints for Affiliates
                            </h4>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-xs">
                                    <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px] font-bold px-1.5 py-0">POST</Badge>
                                    <code className="text-blue-800 font-mono">/lead/v1/public</code>
                                    <span className="text-gray-400">—</span>
                                    <span className="text-gray-600">Create Lead</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                    <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] font-bold px-1.5 py-0">GET</Badge>
                                    <code className="text-blue-800 font-mono">/lead/v1/public/my-leads</code>
                                    <span className="text-gray-400">—</span>
                                    <span className="text-gray-600">Get My Leads</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                    <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] font-bold px-1.5 py-0">GET</Badge>
                                    <code className="text-blue-800 font-mono">/lead/v1/public/status/:leadId</code>
                                    <span className="text-gray-400">—</span>
                                    <span className="text-gray-600">Get Lead Status</span>
                                </div>
                            </div>
                            <p className="text-[11px] text-blue-600 mt-3 pt-2 border-t border-blue-100">
                                Auth header: <code className="bg-blue-100 px-1 py-0.5 rounded text-[10px]">Authorization: Bearer {'<api-key>'}</code>
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Create/Edit API Key Modal */}
            <Dialog open={showCreateApiKeyModal} onOpenChange={handleCloseApiKeyModal}>
                <DialogContent className="max-w-md rounded-xl">
                    <DialogHeader>
                        <DialogTitle className="text-base font-semibold">{generatedApiKey ? "API Key Created" : "Create New API Key"}</DialogTitle>
                        <DialogDescription className="text-xs text-gray-500">
                            {generatedApiKey
                                ? "Copy this API key now. You won't be able to see it again!"
                                : "Generate a new API key for an affiliate. The key will be shown only once."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {!generatedApiKey ? (
                            <>
                                <div>
                                    <Label htmlFor="affiliate-name">Affiliate Name *</Label>
                                    <Input
                                        id="affiliate-name"
                                        value={newApiKeyData.name}
                                        onChange={(e) => setNewApiKeyData({ ...newApiKeyData, name: e.target.value })}
                                        placeholder="e.g., PartnerABC"
                                        disabled={apiKeyLoading}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="default-status">Default Status</Label>
                                    <Select
                                        options={leadStatuses.length > 0
                                            ? leadStatuses.map((status) => ({
                                                label: status.label || status.value || status,
                                                value: status.value || status.label || status
                                            }))
                                            : [
                                                { label: "New", value: "New" },
                                                { label: "Interested", value: "Interested" },
                                                { label: "Follow-up", value: "Follow-up" },
                                                { label: "Converted", value: "Converted" },
                                                { label: "Not Answered", value: "Not Answered" }
                                            ]}
                                        value={newApiKeyData.defaultStatus}
                                        onChange={(value) => setNewApiKeyData({ ...newApiKeyData, defaultStatus: value })}
                                        placeholder="Select default status"
                                        disabled={apiKeyLoading}
                                    />
                                </div>

                                {/* Auto-routing */}
                                <div>
                                    <Label>Auto-route incoming leads</Label>
                                    <Select
                                        options={[
                                            { label: "None — leads stay unassigned", value: "none" },
                                            { label: "Assign to Desk", value: "desk" },
                                            { label: "Round-robin between agents on a Desk", value: "agents" }
                                        ]}
                                        value={newApiKeyData.routingMode}
                                        onChange={(value) => setNewApiKeyData({ ...newApiKeyData, routingMode: value, routingDeskId: value === "none" ? "" : newApiKeyData.routingDeskId })}
                                        placeholder="Select routing mode"
                                        disabled={apiKeyLoading}
                                    />
                                </div>
                                {newApiKeyData.routingMode !== "none" && (
                                    <div>
                                        <Label>Target Desk *</Label>
                                        <Select
                                            options={availableDesks.map((d) => ({
                                                label: d.name + (d.floor ? `  (Floor ${d.floor})` : ""),
                                                value: String(d._id)
                                            }))}
                                            value={newApiKeyData.routingDeskId}
                                            onChange={(value) => setNewApiKeyData({ ...newApiKeyData, routingDeskId: value })}
                                            placeholder={availableDesks.length === 0 ? "No desks available" : "Select desk"}
                                            disabled={apiKeyLoading || availableDesks.length === 0}
                                        />
                                        <p className="text-[11px] text-gray-500 mt-1">
                                            {newApiKeyData.routingMode === "desk"
                                                ? "Every lead from this key will land on this desk. No agent assigned."
                                                : "Each lead is assigned to the next active agent on this desk in strict rotation."}
                                        </p>
                                    </div>
                                )}

                                <div>
                                    <Label>Welcome email template</Label>
                                    <Select
                                        options={[
                                            { label: "Don't send email", value: "" },
                                            ...emailTemplates.map((t) => ({
                                                label: t.isDefault ? `${t.name} (default)` : t.name,
                                                value: String(t._id)
                                            }))
                                        ]}
                                        value={newApiKeyData.emailTemplateId || ""}
                                        onChange={(value) => setNewApiKeyData({ ...newApiKeyData, emailTemplateId: value })}
                                        placeholder="Don't send email"
                                        disabled={apiKeyLoading}
                                    />
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        Sent via company SMTP when this affiliate creates a new lead. Uses {"{{firstName}}"} {"{{lastName}}"} from the lead. Duplicates are not emailed.
                                    </p>
                                </div>

                                <div className="flex space-x-4 pt-4">
                                    <Button
                                        onClick={handleCreateApiKey}
                                        disabled={apiKeyLoading || !newApiKeyData.name.trim()}
                                        className="bg-green-600 hover:bg-green-700 flex-1"
                                    >
                                        {apiKeyLoading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                Creating...
                                            </>
                                        ) : (
                                            "Create API Key"
                                        )}
                                    </Button>
                                    <Button variant="outline" onClick={handleCloseApiKeyModal} disabled={apiKeyLoading}>
                                        Cancel
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                    <Label className="text-green-900 font-medium mb-2 block">
                                        ⚠️ Copy this API key now. You won't be able to see it again!
                                    </Label>
                                    <div className="flex items-center space-x-2">
                                        <Input
                                            value={generatedApiKey}
                                            readOnly
                                            className="font-mono text-sm"
                                        />
                                        <Button
                                            onClick={() => copyToClipboard(generatedApiKey)}
                                            size="sm"
                                            variant="outline"
                                        >
                                            Copy
                                        </Button>
                                    </div>
                                </div>
                                <Button
                                    onClick={handleCloseApiKeyModal}
                                    className="w-full bg-green-600 hover:bg-green-700"
                                >
                                    Done
                                </Button>
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit API Key Modal */}
            <Dialog open={!!editingApiKey} onOpenChange={(open) => { if (!open) closeEditApiKey(); }}>
                <DialogContent className="max-w-md rounded-xl">
                    <DialogHeader>
                        <DialogTitle className="text-base font-semibold">Edit API Key</DialogTitle>
                        <DialogDescription className="text-xs text-gray-500">
                            Update name, default lead status, and auto-routing for this affiliate key.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Affiliate Name *</Label>
                            <Input
                                value={editApiKeyData.name}
                                onChange={(e) => setEditApiKeyData({ ...editApiKeyData, name: e.target.value })}
                                placeholder="e.g., PartnerABC"
                                disabled={apiKeyLoading}
                            />
                        </div>
                        <div>
                            <Label>Default Status</Label>
                            <Select
                                options={leadStatuses.length > 0
                                    ? leadStatuses.map((status) => ({
                                        label: status.label || status.value || status,
                                        value: status.value || status.label || status
                                    }))
                                    : [
                                        { label: "New", value: "New" },
                                        { label: "Interested", value: "Interested" },
                                        { label: "Follow-up", value: "Follow-up" },
                                        { label: "Converted", value: "Converted" },
                                        { label: "Not Answered", value: "Not Answered" }
                                    ]}
                                value={editApiKeyData.defaultStatus}
                                onChange={(value) => setEditApiKeyData({ ...editApiKeyData, defaultStatus: value })}
                                placeholder="Select default status"
                                disabled={apiKeyLoading}
                            />
                        </div>
                        <div>
                            <Label>Auto-route incoming leads</Label>
                            <Select
                                options={[
                                    { label: "None — leads stay unassigned", value: "none" },
                                    { label: "Assign to Desk", value: "desk" },
                                    { label: "Round-robin between agents on a Desk", value: "agents" }
                                ]}
                                value={editApiKeyData.routingMode}
                                onChange={(value) => setEditApiKeyData({ ...editApiKeyData, routingMode: value, routingDeskId: value === "none" ? "" : editApiKeyData.routingDeskId })}
                                placeholder="Select routing mode"
                                disabled={apiKeyLoading}
                            />
                        </div>
                        {editApiKeyData.routingMode !== "none" && (
                            <div>
                                <Label>Target Desk *</Label>
                                <Select
                                    options={availableDesks.map((d) => ({
                                        label: d.name + (d.floor ? `  (Floor ${d.floor})` : ""),
                                        value: String(d._id)
                                    }))}
                                    value={editApiKeyData.routingDeskId}
                                    onChange={(value) => setEditApiKeyData({ ...editApiKeyData, routingDeskId: value })}
                                    placeholder={availableDesks.length === 0 ? "No desks available" : "Select desk"}
                                    disabled={apiKeyLoading || availableDesks.length === 0}
                                />
                                <p className="text-[11px] text-gray-500 mt-1">
                                    {editApiKeyData.routingMode === "desk"
                                        ? "Every lead from this key will land on this desk. No agent assigned."
                                        : "Each lead is assigned to the next active agent on this desk in strict rotation. Counter resets on save."}
                                </p>
                            </div>
                        )}
                        <div>
                            <Label>Welcome email template</Label>
                            <Select
                                options={[
                                    { label: "Don't send email", value: "" },
                                    ...emailTemplates.map((t) => ({
                                        label: t.isDefault ? `${t.name} (default)` : t.name,
                                        value: String(t._id)
                                    }))
                                ]}
                                value={editApiKeyData.emailTemplateId || ""}
                                onChange={(value) => setEditApiKeyData({ ...editApiKeyData, emailTemplateId: value })}
                                placeholder="Don't send email"
                                disabled={apiKeyLoading}
                            />
                            <p className="text-[11px] text-gray-500 mt-1">
                                Sent via company SMTP when this affiliate creates a new lead. Placeholders like {"{{firstName}}"} are filled from the lead.
                            </p>
                        </div>
                        <div className="flex space-x-3 pt-2">
                            <Button
                                onClick={handleSaveApiKeyEdit}
                                disabled={apiKeyLoading || !editApiKeyData.name.trim()}
                                className="bg-green-600 hover:bg-green-700 flex-1"
                            >
                                {apiKeyLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-4 w-4 mr-2" />
                                        Save Changes
                                    </>
                                )}
                            </Button>
                            <Button variant="outline" onClick={closeEditApiKey} disabled={apiKeyLoading}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
